import React, { useState, useRef, useEffect } from 'react';
import { mergeYearGoals, mergeYearGoalNodes, formatGoalCurrency } from "../lib/goalWording";
import { Link } from 'react-router-dom';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { rememberUser } from '../hooks/useUserState';
import VictoryLapScreen from '../components/VictoryLapScreen';
import { Target, Lightning as Zap, Calendar, ArrowRight, ArrowLeft, DownloadSimple as Download, CurrencyDollar as DollarSign, Clock, PiggyBank, CreditCard, CaretRight as ChevronRight, Copy, Check, Palette, Eye, CheckCircle as CheckCircle2, Sparkle as Sparkles, CircleNotch as Loader2, HouseLine as Home } from "@phosphor-icons/react";
import { toast } from 'sonner';
import { trackEvent, TikTokIcon, ThreadsIcon, PATHS, WEEKS_OFF_OPTIONS, SELF_EMPLOYMENT_TAX } from '../lib/incomeMapShared';
import CompletionScreen from '../components/income-map/CompletionScreen';
import CreatorFooter from '../components/income-map/CreatorFooter';
import { useBetaGate } from '../components/BetaCheckoutModal';
import { BetaBadge } from '../components/BetaBadge';

const API_URL = process.env.REACT_APP_BACKEND_URL;


const LandingPage = () => {
  // Purchase tracking state
  const [purchases, setPurchases] = useState({
    momentumMap: false,      // $4.95 Income Mapping Results
    fourRelay: false,        // $9 Four Relay Challenge  
    fullPaydayRelay: false   // $54 Full Payday Relay
  });
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);
  const [currentPaymentProduct, setCurrentPaymentProduct] = useState(null);
  const [showThankYou, setShowThankYou] = useState(false);
  const [lastPurchasedProduct, setLastPurchasedProduct] = useState(null);
  
  // Access control state
  const [hasPaidAccess, setHasPaidAccess] = useState(true); // Income Map is FREE, part of the Sprint (pivot Aug 2026)
  const [isCheckingPayment, setIsCheckingPayment] = useState(true);
  const [hasDownloadedPDF, setHasDownloadedPDF] = useState(false);
  const [isDownloadingPDF, setIsDownloadingPDF] = useState(false);
  
  // Calculator state
  const [currentStep, setCurrentStep] = useState(0);
  const [selectedPaths, setSelectedPaths] = useState([]);
  const [formData, setFormData] = useState({
    monthlyExpenses: '',
    // Target mode - user has a specific monthly income target
    hasTargetInMind: false,
    entryPathSelected: false, // Track if user has chosen their entry path
    monthlyTarget: '',
    // Debt freedom
    debtAmount: '',
    debtMonths: '', // months to pay off
    // Savings
    savingsGoal: '',
    savingsMonths: '', // months to save
    // Time freedom / Weeks off
    weeksOff: 4,
    customWeeksOff: '',
    // Days per week
    daysPerWeek: 5,
    // Taxes
    federalTaxRate: 10, // 0-15%
    // Buffer for slow weeks (expenses path only)
    slowWeekBuffer: 10 // 0-30%
  });
  const [showResults, setShowResults] = useState(false);
  const [placardColor, setPlacardColor] = useState('#2A1E5C');
  const [placardFormat, setPlacardFormat] = useState('landscape'); // landscape, portrait, story
  const [copied, setCopied] = useState(false);
  const [goalCopied, setGoalCopied] = useState(false);
  const [showSharePreview, setShowSharePreview] = useState(false);
  const [emailInput, setEmailInput] = useState('');
  const [emailSending, setEmailSending] = useState(false);
  const [showVictoryLap, setShowVictoryLap] = useState(false);
  const [emailSent, setEmailSent] = useState(() => sessionStorage.getItem('email_sent') === 'true');
  const [upsellFlowComplete, setUpsellFlowComplete] = useState(() => sessionStorage.getItem('upsell_flow_complete') === 'true');
  const [showUpsellModal, setShowUpsellModal] = useState(() => {
    // If email was sent but upsell flow not completed, show the appropriate modal
    const emailDone = sessionStorage.getItem('email_sent') === 'true';
    const upsellFlowDone = sessionStorage.getItem('upsell_flow_complete') === 'true';
    const viewingDownsell = sessionStorage.getItem('viewing_downsell') === 'true';
    return emailDone && !upsellFlowDone && !viewingDownsell;
  });
  const [showDownsellModal, setShowDownsellModal] = useState(() => {
    // Show downsell modal if they were viewing it before navigating away
    const emailDone = sessionStorage.getItem('email_sent') === 'true';
    const upsellFlowDone = sessionStorage.getItem('upsell_flow_complete') === 'true';
    const viewingDownsell = sessionStorage.getItem('viewing_downsell') === 'true';
    return emailDone && !upsellFlowDone && viewingDownsell;
  });
  const [sanityCheckExpenses, setSanityCheckExpenses] = useState(''); // For Path A users to optionally add expenses
  const placardRef = useRef(null);
  const { betaMode, openGate, gateModal } = useBetaGate();

  // Product definitions
  const PRODUCTS = {
    momentumMap: {
      id: 'momentum_map',
      name: 'Income Mapping Results',
      price: 4.95,
      description: 'Your map: personalized PDF with your weekly target and 12-month trajectory',
      stripeLink: 'https://buy.stripe.com/4gM28rdat7o33DufePcfK04',
      image: '/images/income-momentum-map.webp'
    },
    fourRelay: {
      id: 'four_relay',
      name: '4-Relay Challenge',
      price: 9,
      description: 'A taste of Payday Relay: try the weekly rhythm for 4 weeks',
      stripeLink: 'https://buy.stripe.com/4gMaEXb2l7o3b5WeaLcfK05',
      image: '/images/4-relay-challenge.webp'
    },
    fullPaydayRelay: {
      id: 'full_payday_relay',
      name: 'Payday Relay',
      price: 54,
      description: 'Your engine: the complete 52-week system to make your goals a reality',
      stripeLink: 'https://buy.stripe.com/9B6bJ15I1dMrgqg9UvcfK06',
      image: '/images/payday-relay-1.webp'
    }
  };

  // Check for payment on page load
  useEffect(() => {
    const checkPaymentStatus = async () => {
      setIsCheckingPayment(true);
      const urlParams = new URLSearchParams(window.location.search);
      const sessionId = urlParams.get('session_id');
      const paymentStatus = urlParams.get('payment');
      const paymentSuccess = urlParams.get('payment_success');
      const product = urlParams.get('product');
      
      // Check sessionStorage for current session access (expires when browser closes)
      const hasMomentumMap = sessionStorage.getItem('purchased_momentum_map') === 'true';
      const hasFourRelay = sessionStorage.getItem('purchased_four_relay') === 'true';
      const hasFullPaydayRelay = sessionStorage.getItem('purchased_full_payday_relay') === 'true';
      const alreadyDownloaded = sessionStorage.getItem('downloaded_pdf') === 'true';
      
      setPurchases({
        momentumMap: hasMomentumMap,
        fourRelay: hasFourRelay,
        fullPaydayRelay: hasFullPaydayRelay
      });
      
      // Grant access if user has purchased the momentum map (unlocks calculator)
      if (hasMomentumMap) {
        setHasPaidAccess(true);
        setHasDownloadedPDF(alreadyDownloaded);
      }
      
      // Handle direct Stripe payment link redirect (payment_success=true)
      if (paymentSuccess === 'true' && product) {
        // Track Meta Pixel Purchase event
        if (typeof window.fbq === 'function') {
          window.fbq('track', 'Purchase', {
            value: 4.95,
            currency: 'USD',
            content_name: 'Income Mapping Results'
          });
        }
        
        // Direct payment link success - grant access immediately
        if (product === 'momentum_map') {
          setPurchases(prev => ({ ...prev, momentumMap: true }));
          sessionStorage.setItem('purchased_momentum_map', 'true');
          setHasPaidAccess(true);
        } else if (product === 'four_relay') {
          setPurchases(prev => ({ ...prev, fourRelay: true }));
          sessionStorage.setItem('purchased_four_relay', 'true');
        } else if (product === 'full_payday_relay') {
          setPurchases(prev => ({ ...prev, fullPaydayRelay: true }));
          sessionStorage.setItem('purchased_full_payday_relay', 'true');
        }
        
        setLastPurchasedProduct(product);
        setIsCheckingPayment(false);
        
        // Reset download flag - new payment = new download allowed
        sessionStorage.removeItem('downloaded_pdf');
        sessionStorage.removeItem('email_sent');
        setHasDownloadedPDF(false);
        setEmailSent(false);
        
        // Clean up URL
        window.history.replaceState({}, '', window.location.pathname);
        
        // Scroll to calculator after unlocking
        setTimeout(() => {
          document.getElementById('calculator')?.scrollIntoView({ behavior: 'smooth' });
        }, 300);
        return;
      }
      
      // Handle API-based checkout redirect (session_id + payment=success)
      if (sessionId && paymentStatus === 'success') {
        // Poll for payment status
        let attempts = 0;
        const maxAttempts = 10;
        
        const pollStatus = async () => {
          try {
            const response = await fetch(`${API_URL}/api/payments/status/${sessionId}`);
            const data = await response.json();
            
            if (data.payment_status === 'paid') {
              // Update purchases based on product
              if (product === 'momentum_map') {
                setPurchases(prev => ({ ...prev, momentumMap: true }));
                sessionStorage.setItem('purchased_momentum_map', 'true');
                setHasPaidAccess(true); // Unlock calculator access
              } else if (product === 'four_relay') {
                setPurchases(prev => ({ ...prev, fourRelay: true }));
                sessionStorage.setItem('purchased_four_relay', 'true');
              } else if (product === 'full_payday_relay') {
                setPurchases(prev => ({ ...prev, fullPaydayRelay: true }));
                sessionStorage.setItem('purchased_full_payday_relay', 'true');
              }
              
              setLastPurchasedProduct(product);
              setShowThankYou(true);
              setIsCheckingPayment(false);
              
              // Clean up URL
              window.history.replaceState({}, '', window.location.pathname);
              
              // Scroll to calculator after unlocking
              setTimeout(() => {
                document.getElementById('calculator')?.scrollIntoView({ behavior: 'smooth' });
              }, 300);
              return;
            }
            
            attempts++;
            if (attempts < maxAttempts) {
              setTimeout(pollStatus, 2000);
            } else {
              toast.error('Could not verify payment. Please contact support.');
              setIsCheckingPayment(false);
            }
          } catch (error) {
            console.error('Error checking payment:', error);
            setIsCheckingPayment(false);
          }
        };
        
        pollStatus();
      } else if (paymentStatus === 'cancelled') {
        toast.info('Payment was cancelled. You can try again anytime.');
        window.history.replaceState({}, '', window.location.pathname);
        setIsCheckingPayment(false);
      } else {
        setIsCheckingPayment(false);
      }
    };
    
    checkPaymentStatus();
  }, []);

  // Handle payment initiation for any product
  const handleStartPayment = async (productKey) => {
    if (betaMode) {
      openGate(() => proceedToStripe(productKey));
      return;
    }
    proceedToStripe(productKey);
  };

  const proceedToStripe = async (productKey) => {
    setIsProcessingPayment(true);
    setCurrentPaymentProduct(productKey);
    
    const product = PRODUCTS[productKey];
    
    try {
      // Track analytics before redirect
      await trackEvent('payment_initiated', { product_id: product.id, price: product.price });
      
      // Track Meta Pixel InitiateCheckout event
      if (typeof window.fbq === 'function') {
        window.fbq('track', 'InitiateCheckout', {
          value: product.price,
          currency: 'USD',
          content_name: product.name
        });
      }
      
      // Redirect directly to Stripe payment link
      window.location.href = product.stripeLink;
    } catch (error) {
      console.error('Payment error:', error);
      toast.error('Could not start payment. Please try again.');
      setIsProcessingPayment(false);
    }
  };

  const accentColors = [
    { name: 'Purple', hex: '#2A1E5C' },
    { name: 'Teal', hex: '#2E86AB' },
    { name: 'Coral', hex: '#DE3C4B' },
    { name: 'Gold', hex: '#F5CB5C' },
    { name: 'Deep Teal', hex: '#114B5F' }
  ];

  // Calculate NET weekly target (what you need to take home)
  const calculateNetWeeklyTarget = () => {
    const workWeeks = getWorkingWeeks();
    
    // Start with base need
    let annualNeed = 0;
    
    // If user has a specific monthly target in mind, that's their BASE
    if (formData.hasTargetInMind && formData.monthlyTarget) {
      const monthlyTarget = parseFloat(formData.monthlyTarget) || 0;
      // The target IS the take-home (net). We gross it up on top with taxes.
      const monthlyNet = monthlyTarget;
      annualNeed = monthlyNet * 12;
    } else {
      // Otherwise use monthly expenses as the base
      const monthlyExpenses = parseFloat(formData.monthlyExpenses) || 0;
      annualNeed = monthlyExpenses * 12;
    }
    
    // TARGET PATH: The monthly target IS the target. Debt / savings / PTO are
    // allocations carved OUT of that take-home, not added on top. The sanity
    // check surfaces a warning if the allocations don't fit.
    //
    // EXPENSES PATH: Debt and savings are extra commitments layered on top of
    // the user's monthly expense floor, so they grow the weekly target.
    if (!formData.hasTargetInMind) {
      if (formData.debtAmount && parseFloat(formData.debtAmount) > 0 && formData.debtMonths) {
        const debtAmount = parseFloat(formData.debtAmount) || 0;
        const debtMonths = parseInt(formData.debtMonths) || 12;
        const workingWeeksInDebtPeriod = (debtMonths / 12) * workWeeks;
        const debtAnnualAddition = (debtAmount / workingWeeksInDebtPeriod) * workWeeks;
        annualNeed += debtAnnualAddition;
      }
      
      if (formData.savingsGoal && parseFloat(formData.savingsGoal) > 0 && formData.savingsMonths) {
        const savingsGoal = parseFloat(formData.savingsGoal) || 0;
        const savingsMonths = parseInt(formData.savingsMonths) || 12;
        const workingWeeksInSavingsPeriod = (savingsMonths / 12) * workWeeks;
        const savingsAnnualAddition = (savingsGoal / workingWeeksInSavingsPeriod) * workWeeks;
        annualNeed += savingsAnnualAddition;
      }
    }
    
    // Divide total annual need by working weeks = NET weekly target
    return annualNeed / workWeeks;
  };

  // Calculate GROSS weekly target (what you need to earn before taxes)
  const calculateGrossWeeklyTarget = () => {
    const netWeekly = calculateNetWeeklyTarget();
    const federalTaxRate = (formData.federalTaxRate || 0) / 100;
    const totalTaxRate = SELF_EMPLOYMENT_TAX + federalTaxRate;
    
    // Gross = Net / (1 - tax rate)
    return netWeekly / (1 - totalTaxRate);
  };

  // This is the main "weekly income target" - what they need to GROSS
  const calculateRelayTarget = () => {
    const baseGross = calculateGrossWeeklyTarget();
    
    // Apply buffer for expenses path (not target path)
    if (!formData.hasTargetInMind && formData.slowWeekBuffer > 0) {
      const bufferMultiplier = 1 + (formData.slowWeekBuffer / 100);
      return baseGross * bufferMultiplier;
    }
    
    return baseGross;
  };

  // Calculate daily target (gross)
  const calculateDailyTarget = () => {
    const weeklyTarget = calculateRelayTarget();
    const daysPerWeek = parseInt(formData.daysPerWeek) || 5;
    return weeklyTarget / daysPerWeek;
  };

  // Calculate tax amounts - uses the BUFFERED gross target for consistency
  const getTaxBreakdown = () => {
    const grossWeekly = calculateRelayTarget(); // Use buffered amount
    const federalTaxRate = (formData.federalTaxRate || 0) / 100;
    
    const selfEmploymentTax = grossWeekly * SELF_EMPLOYMENT_TAX;
    const federalTax = grossWeekly * federalTaxRate;
    const totalTax = selfEmploymentTax + federalTax;
    const netWeekly = grossWeekly * (1 - SELF_EMPLOYMENT_TAX - federalTaxRate);
    
    return { selfEmploymentTax, federalTax, totalTax, netWeekly, grossWeekly };
  };

  // Get breakdown of weekly target
  // Note: Check formData directly, not selectedPaths (which may not be updated yet during render)
  const getBreakdown = () => {
    const workWeeks = getWorkingWeeks();
    
    // If user has a specific monthly target, the breakdown is different
    if (formData.hasTargetInMind && formData.monthlyTarget) {
      const monthlyTarget = parseFloat(formData.monthlyTarget) || 0;
      const grossWeekly = (monthlyTarget * 12) / workWeeks;
      return { expensesWeekly: grossWeekly, debtWeekly: 0, savingsWeekly: 0, isTargetMode: true };
    }
    
    const monthlyExpenses = parseFloat(formData.monthlyExpenses) || 0;
    const expensesWeekly = (monthlyExpenses * 12) / workWeeks;
    
    let debtWeekly = 0;
    if (formData.debtAmount && parseFloat(formData.debtAmount) > 0 && formData.debtMonths) {
      const debtAmount = parseFloat(formData.debtAmount) || 0;
      const debtMonths = parseInt(formData.debtMonths) || 12;
      // Calculate working weeks in debt payoff period
      const workingWeeksInDebtPeriod = (debtMonths / 12) * workWeeks;
      debtWeekly = debtAmount / workingWeeksInDebtPeriod;
    }
    
    let savingsWeekly = 0;
    if (formData.savingsGoal && parseFloat(formData.savingsGoal) > 0 && formData.savingsMonths) {
      const savingsGoal = parseFloat(formData.savingsGoal) || 0;
      const savingsMonths = parseInt(formData.savingsMonths) || 12;
      // Calculate working weeks in savings period
      const workingWeeksInSavingsPeriod = (savingsMonths / 12) * workWeeks;
      savingsWeekly = savingsGoal / workingWeeksInSavingsPeriod;
    }
    
    return { expensesWeekly, debtWeekly, savingsWeekly, isTargetMode: false };
  };

  const formatCurrency = (num) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(num);
  };

  const getWorkingWeeks = () => {
    const weeksOff = formData.weeksOff === 'custom' 
      ? parseInt(formData.customWeeksOff) || 0 
      : formData.weeksOff;
    return 52 - weeksOff;
  };

  const getWeeksOff = () => {
    return formData.weeksOff === 'custom' 
      ? parseInt(formData.customWeeksOff) || 0 
      : formData.weeksOff;
  };

  // Calculate time-off buffer per week
  // The PTO bank funds NET weekly income during weeks off (your lifestyle, not gross earnings)
  const getTimeOffBuffer = () => {
    const weeksOff = getWeeksOff();
    if (weeksOff <= 0) return 0;
    
    // Use net weekly target - this represents your actual take-home lifestyle needs
    // regardless of whether user entered a monthly target or started with expenses
    const netWeeklyTarget = calculateNetWeeklyTarget();
    
    // Total needed for time off = net weekly target × weeks off
    // Spread over working weeks = amount to set aside each working week
    return (netWeeklyTarget * weeksOff) / getWorkingWeeks();
  };

  // SMART Feasibility Check - determines if the user's plan is realistic
  const getFeasibilityCheck = () => {
    const workWeeks = getWorkingWeeks();
    
    // Get expenses - either from original form or sanity check input (for Path A users)
    const monthlyExpenses = formData.hasTargetInMind 
      ? (parseFloat(sanityCheckExpenses) || 0)
      : (parseFloat(formData.monthlyExpenses) || 0);
    
    const hasExpenses = monthlyExpenses > 0;
    
    // Calculate annual net income capacity
    const grossWeekly = calculateRelayTarget();
    const federalTaxRate = (formData.federalTaxRate || 0) / 100;
    const totalTaxRate = SELF_EMPLOYMENT_TAX + federalTaxRate;
    const netWeekly = grossWeekly * (1 - totalTaxRate);
    const annualNetIncome = netWeekly * workWeeks;
    
    // Calculate annual commitments
    const annualExpenses = monthlyExpenses * 12;
    const debtAmount = (selectedPaths.includes('debt') && formData.debtAmount) 
      ? parseFloat(formData.debtAmount) || 0 
      : 0;
    const savingsAmount = (selectedPaths.includes('savings') && formData.savingsGoal) 
      ? parseFloat(formData.savingsGoal) || 0 
      : 0;
    
    // Check if user has any stretch goals (debt or savings)
    const hasStretchGoals = debtAmount > 0 || savingsAmount > 0;
    
    // For Path A (target-first), debt/savings/PTO are carved OUT of the target's
    // take-home, so max allowable expenses = net(target) minus those allocations.
    const monthlyTargetNum = parseFloat(formData.monthlyTarget) || 0;
    const targetMonthlyNet = monthlyTargetNum * (1 - totalTaxRate);
    const monthlyDebtAllocation = (debtAmount > 0 && formData.debtMonths)
      ? debtAmount / (parseInt(formData.debtMonths) || 12)
      : 0;
    const monthlySavingsAllocation = (savingsAmount > 0 && formData.savingsMonths)
      ? savingsAmount / (parseInt(formData.savingsMonths) || 12)
      : 0;
    const monthlyPtoAllocation = getWeeksOff() > 0
      ? (getTimeOffBuffer() * (52 - getWeeksOff())) / 12
      : 0;
    const monthlyGoalAllocations = monthlyDebtAllocation + monthlySavingsAllocation + monthlyPtoAllocation;
    const maxMonthlyExpenses = Math.max(0, targetMonthlyNet - monthlyGoalAllocations);
    
    // If no expenses provided (Path A without sanity check), return constraint-based guidance
    if (!hasExpenses && formData.hasTargetInMind) {
      const goalParts = [];
      if (monthlyDebtAllocation > 0) goalParts.push(`${formatCurrency(monthlyDebtAllocation)}/mo debt`);
      if (monthlySavingsAllocation > 0) goalParts.push(`${formatCurrency(monthlySavingsAllocation)}/mo savings`);
      if (monthlyPtoAllocation > 0) goalParts.push(`${formatCurrency(monthlyPtoAllocation)}/mo time-off fund`);
      const suggestion = goalParts.length > 0
        ? `That leaves room after covering ${goalParts.join(' + ')} out of your take-home.`
        : null;
      return {
        status: 'unknown',
        hasExpenses: false,
        maxMonthlyExpenses,
        message: `To support this plan, your monthly expenses would need to stay around ${formatCurrency(maxMonthlyExpenses)} or lower, that's your ${formatCurrency(monthlyTargetNum)}/month target after taxes and allocations.`,
        suggestion
      };
    }
    
    // Calculate total commitments and buffer
    // Include time-off fund in commitments since it's money that's spoken for
    const workWeeksForCalc = 52 - getWeeksOff();
    const timeOffFundTotal = getTimeOffBuffer() * workWeeksForCalc; // Total PTO fund for the year
    const totalAnnualCommitments = annualExpenses + debtAmount + savingsAmount + timeOffFundTotal;
    const surplus = annualNetIncome - totalAnnualCommitments;
    const calculatedBufferPercent = totalAnnualCommitments > 0 
      ? (surplus / totalAnnualCommitments) * 100 
      : 100;
    
    // For expenses path, use the user's chosen buffer, not calculated
    const bufferPercent = !formData.hasTargetInMind ? formData.slowWeekBuffer : calculatedBufferPercent;
    
    // Calculate what income would be needed to make this realistic (with 20% buffer)
    const requiredAnnualNet = totalAnnualCommitments * 1.2;
    const requiredMonthlyGross = (requiredAnnualNet / 12) / (1 - totalTaxRate);
    
    // Classify status based on buffer and path
    let status, message, suggestion;
    
    // EXPENSES PATH: Status is based on user's chosen buffer
    if (!formData.hasTargetInMind) {
      if (formData.slowWeekBuffer === 0) {
        status = 'not-realistic';
        message = `With a 0% buffer, this is your exact minimum with zero wiggle room. One slow week could throw everything off.`;
        suggestion = 'Consider adding at least a small buffer.';
      } else if (formData.slowWeekBuffer === 5) {
        status = 'tight';
        message = `With a 5% buffer, you've given yourself a tiny bit of breathing room, but a bad week could still set you back.`;
        suggestion = 'Consider increasing your buffer.';
      } else if (formData.slowWeekBuffer === 10) {
        status = 'on-track';
        message = `With a 10% buffer, you can handle the occasional slow week.`;
        suggestion = null;
      } else if (formData.slowWeekBuffer === 15) {
        status = 'solid';
        message = `With a 15% buffer, you've got real breathing room for inconsistent weeks.`;
        suggestion = null;
      } else if (formData.slowWeekBuffer === 20) {
        status = 'nice';
        message = `With a 20% buffer, you can weather a rough month without stress.`;
        suggestion = null;
      } else if (formData.slowWeekBuffer === 25) {
        status = 'great';
        message = `With a 25% buffer, you've got serious cushion for inconsistent income.`;
        suggestion = null;
      } else if (formData.slowWeekBuffer >= 30) {
        status = 'perfect';
        message = `With a 30% buffer, you're set for emergencies and slow seasons.`;
        suggestion = null;
      } else {
        // Fallback for any other value
        status = 'on-track';
        message = `With a ${formData.slowWeekBuffer}% buffer, you've got some breathing room.`;
        suggestion = null;
      }
    }
    // TARGET PATH: Status is based on calculated buffer
    else if (calculatedBufferPercent >= 20) {
      status = 'on-track';
      message = `Your plan is realistic with a healthy ${Math.round(calculatedBufferPercent)}% buffer.`;
      suggestion = null;
    } else if (calculatedBufferPercent >= 0) {
      status = 'tight';
      message = `This plan works, but leaves little room for slow weeks (${Math.round(calculatedBufferPercent)}% buffer).`;
      suggestion = 'Consider extending your timeline or slightly increasing your target to build more cushion.';
    } else {
      status = 'not-realistic';
      message = `This plan currently doesn't add up. Your commitments exceed your income capacity.`;
      suggestion = `To make this work, you'd need to earn approximately ${formatCurrency(requiredMonthlyGross)}/month, or adjust your goals.`;
    }
    
    return {
      status,
      hasExpenses: true,
      bufferPercent: Math.round(bufferPercent),
      annualNetIncome,
      totalAnnualCommitments,
      surplus,
      requiredMonthlyGross,
      hasStretchGoals,
      message,
      suggestion
    };
  };

  // Build goal statement parts with timelines
  // Note: Check formData directly, not selectedPaths (which may not be updated yet during render)
  const getGoalParts = () => {
    const parts = [];
    
    // If user has a specific monthly target, use different language
    if (formData.hasTargetInMind && formData.monthlyTarget) {
      parts.push(`hit my ${formatGoalCurrency(parseFloat(formData.monthlyTarget))}/month income target`);
    } else {
      // Always include covering expenses
      parts.push('cover my monthly expenses');
    }
    
    if (formData.debtAmount && parseFloat(formData.debtAmount) > 0 && formData.debtMonths) {
      const months = parseInt(formData.debtMonths);
      const timeframe = months === 12 ? 'in 1 year' : `in ${months} months`;
      parts.push(`pay off ${formatGoalCurrency(parseFloat(formData.debtAmount))} ${timeframe}`);
    }
    
    if (formData.savingsGoal && parseFloat(formData.savingsGoal) > 0 && formData.savingsMonths) {
      const months = parseInt(formData.savingsMonths);
      const timeframe = months === 12 ? 'in 1 year' : `in ${months} months`;
      parts.push(`save ${formatGoalCurrency(parseFloat(formData.savingsGoal))} ${timeframe}`);
    }
    
    if (getWeeksOff() > 0) {
      parts.push(`take ${getWeeksOff()} weeks off this year`);
    }
    
    return mergeYearGoals(parts);
  };

  const getGoalStatement = () => {
    const target = calculateRelayTarget();
    const parts = getGoalParts();
    const workWeeks = getWorkingWeeks();
    
    if (parts.length === 1) {
      return `I need to earn ${formatGoalCurrency(target)} every week for ${workWeeks} weeks so I can ${parts[0]}.`;
    } else if (parts.length === 2) {
      return `I need to earn ${formatGoalCurrency(target)} every week for ${workWeeks} weeks so I can ${parts[0]} and ${parts[1]}.`;
    } else {
      const lastPart = parts.pop();
      return `I need to earn ${formatGoalCurrency(target)} every week for ${workWeeks} weeks so I can ${parts.join(', ')}, and ${lastPart}.`;
    }
  };

  // JSX version with bold numbers for display
  // Note: Check formData directly, not selectedPaths (which may not be updated yet during render)
  const getGoalStatementJSX = () => {
    const target = calculateRelayTarget();
    const workWeeks = getWorkingWeeks();
    
    // Build parts with bold numbers
    const goalItems = [];
    
    // Monthly target or expenses
    if (formData.hasTargetInMind && formData.monthlyTarget) {
      goalItems.push({ key: "target", year: false, node: <span>hit my <strong>{formatGoalCurrency(parseFloat(formData.monthlyTarget))}/month</strong> income target</span> });
    } else {
      goalItems.push({ key: "expenses", year: false, node: <span>cover my monthly expenses</span> });
    }
    
    // Debt
    if (formData.debtAmount && parseFloat(formData.debtAmount) > 0 && formData.debtMonths) {
      const months = parseInt(formData.debtMonths);
      const timeframe = months === 12 ? 'in 1 year' : `in ${months} months`;
      goalItems.push({ key: "debt", year: months === 12, node: <span>pay off <strong>{formatGoalCurrency(parseFloat(formData.debtAmount))}</strong>{months === 12 ? "" : ` ${timeframe}`}</span> });
    }
    
    // Savings
    if (formData.savingsGoal && parseFloat(formData.savingsGoal) > 0 && formData.savingsMonths) {
      const months = parseInt(formData.savingsMonths);
      const timeframe = months === 12 ? 'in 1 year' : `in ${months} months`;
      goalItems.push({ key: "savings", year: months === 12, node: <span>save <strong>{formatGoalCurrency(parseFloat(formData.savingsGoal))}</strong>{months === 12 ? "" : ` ${timeframe}`}</span> });
    }
    
    // Time off
    if (getWeeksOff() > 0) {
      goalItems.push({ key: "time", year: true, node: <span>take <strong>{getWeeksOff()} weeks</strong> off</span> });
    }
    
    // Build the sentence
    const goalParts = mergeYearGoalNodes(goalItems);
    const renderParts = () => {
      if (goalParts.length === 1) {
        return goalParts[0];
      } else if (goalParts.length === 2) {
        return <>{goalParts[0]} and {goalParts[1]}</>;
      } else {
        const allButLast = goalParts.slice(0, -1);
        const last = goalParts[goalParts.length - 1];
        return <>{allButLast.map((part, i) => <span key={`goal-part-${i}`}>{part}{i < allButLast.length - 1 ? ', ' : ''}</span>)}, and {last}</>;
      }
    };
    
    return (
      <>
        I need to earn <strong>{formatGoalCurrency(target)}</strong> every week for <strong>{workWeeks} weeks</strong> so I can {renderParts()}.
      </>
    );
  };

  // ========== TIME-PHASED TIMELINE LOGIC ==========
  
  // Get all goals with their timelines
  // Note: Check formData directly, not selectedPaths (which may not be updated yet during render)
  const getGoalTimelines = () => {
    const timelines = [];
    
    // Debt goal
    if (formData.debtAmount && parseFloat(formData.debtAmount) > 0 && formData.debtMonths) {
      const debtAmount = parseFloat(formData.debtAmount) || 0;
      const debtMonths = parseInt(formData.debtMonths) || 12;
      const workWeeks = getWorkingWeeks();
      // Calculate working weeks in the debt payoff period (proportional to the year)
      const workingWeeksInDebtPeriod = (debtMonths / 12) * workWeeks;
      const weeklyAmount = debtAmount / workingWeeksInDebtPeriod;
      
      timelines.push({
        id: 'debt',
        name: 'Debt Payoff',
        endMonth: debtMonths,
        weeklyAmount: weeklyAmount,
        totalAmount: debtAmount,
        color: '#DE3C4B',
        icon: 'CreditCard'
      });
    }
    
    // Savings goal
    if (formData.savingsGoal && parseFloat(formData.savingsGoal) > 0 && formData.savingsMonths) {
      const savingsGoal = parseFloat(formData.savingsGoal) || 0;
      const savingsMonths = parseInt(formData.savingsMonths) || 12;
      const workWeeks = getWorkingWeeks();
      // Calculate working weeks in the savings period (proportional to the year)
      const workingWeeksInSavingsPeriod = (savingsMonths / 12) * workWeeks;
      const weeklyAmount = savingsGoal / workingWeeksInSavingsPeriod;
      
      timelines.push({
        id: 'savings',
        name: 'Savings Goal',
        endMonth: savingsMonths,
        weeklyAmount: weeklyAmount,
        totalAmount: savingsGoal,
        color: '#2E86AB',
        icon: 'PiggyBank'
      });
    }
    
    return timelines.sort((a, b) => a.endMonth - b.endMonth);
  };

  // Generate phases based on when goals end (capped at 12 months)
  const generatePhases = () => {
    const timelines = getGoalTimelines();
    if (timelines.length === 0) return [];
    
    const workWeeks = getWorkingWeeks();
    const daysPerWeek = parseInt(formData.daysPerWeek) || 5;
    const federalTaxRate = (formData.federalTaxRate || 0) / 100;
    const totalTaxRate = SELF_EMPLOYMENT_TAX + federalTaxRate;
    
    // In TARGET MODE: Monthly target is the BASE, debt/savings are ADDED on top
    // As goals complete, the weekly target DECREASES
    if (formData.hasTargetInMind && formData.monthlyTarget) {
      // Get unique end months (cap at 12)
      const endMonths = [...new Set(timelines.map(t => Math.min(t.endMonth, 12)))].sort((a, b) => a - b);
      
      const phases = [];
      let startMonth = 1;
      let activeGoals = [...timelines];
      
      // For phase 1, use the main calculation to ensure consistency (including buffer)
      const phase1GrossWeekly = calculateRelayTarget();
      const phase1GrossDaily = calculateDailyTarget();
      const phase1NetWeekly = phase1GrossWeekly * (1 - totalTaxRate);
      
      endMonths.forEach((endMonth, index) => {
        let grossWeekly, grossDaily, netWeekly;
        
        if (index === 0) {
          // Phase 1: Use main calculation for consistency
          grossWeekly = phase1GrossWeekly;
          grossDaily = phase1GrossDaily;
          netWeekly = phase1NetWeekly;
        } else {
          // Subsequent phases: Calculate reduction from goals completing.
          // In target-mode, goal weekly amounts are added as gross (no tax gross-up),
          // so we pre-scale by (1 - taxRate) before subtracting from netWeekly so the
          // subsequent gross-up cancels out, the goal comes off the gross target cleanly.
          const completedGoalsAmount = timelines
            .filter(g => g.endMonth <= endMonths[index - 1])
            .reduce((sum, g) => sum + g.weeklyAmount, 0);
          netWeekly = phase1NetWeekly - completedGoalsAmount * (1 - totalTaxRate);
          grossWeekly = netWeekly / (1 - totalTaxRate);
          grossDaily = grossWeekly / daysPerWeek;
        }
        
        const completingGoals = activeGoals.filter(g => g.endMonth === endMonth);
        
        phases.push({
          phaseNumber: index + 1,
          startMonth,
          endMonth,
          netWeekly,
          grossWeekly,
          grossDaily,
          activeGoals: [...activeGoals],
          completingGoals,
          isLastPhase: index === endMonths.length - 1
        });
        
        activeGoals = activeGoals.filter(g => g.endMonth > endMonth);
        startMonth = endMonth + 1;
      });
      
      return phases;
    }
    
    // EXPENSES MODE: Base expenses + goals added on top
    // Get unique end months (cap at 12)
    const endMonths = [...new Set(timelines.map(t => Math.min(t.endMonth, 12)))].sort((a, b) => a - b);
    
    const phases = [];
    let startMonth = 1;
    let activeGoals = [...timelines];
    
    // For phase 1, use the main calculation to ensure consistency (including buffer)
    const phase1GrossWeekly = calculateRelayTarget();
    const phase1GrossDaily = calculateDailyTarget();
    const phase1NetWeekly = phase1GrossWeekly * (1 - totalTaxRate);
    
    endMonths.forEach((endMonth, index) => {
      let grossWeekly, grossDaily, netWeekly;
      
      if (index === 0) {
        // Phase 1: Use main calculation for consistency
        grossWeekly = phase1GrossWeekly;
        grossDaily = phase1GrossDaily;
        netWeekly = phase1NetWeekly;
      } else {
        // Subsequent phases: Calculate reduction from goals completing
        const completedGoalsAmount = timelines
          .filter(g => g.endMonth <= endMonths[index - 1])
          .reduce((sum, g) => sum + g.weeklyAmount, 0);
        netWeekly = phase1NetWeekly - completedGoalsAmount;
        grossWeekly = netWeekly / (1 - totalTaxRate);
        grossDaily = grossWeekly / daysPerWeek;
      }
      
      // Goals completing at this phase end
      const completingGoals = activeGoals.filter(g => g.endMonth === endMonth);
      
      phases.push({
        phaseNumber: index + 1,
        startMonth,
        endMonth,
        netWeekly,
        grossWeekly,
        grossDaily,
        activeGoals: [...activeGoals],
        completingGoals,
        isLastPhase: index === endMonths.length - 1
      });
      
      // Remove completed goals for next phase
      activeGoals = activeGoals.filter(g => g.endMonth > endMonth);
      startMonth = endMonth + 1;
    });
    
    return phases;
  };

  // Calculate the three momentum options within the 12-month container
  const calculateMomentumOptions = () => {
    const phases = generatePhases().filter(p => !p.isOngoing);
    if (phases.length < 2) return null;
    
    const phase1 = phases[0]; // Foundation phase (highest target)
    const phase2 = phases[1]; // Momentum phase (after first goal completes)
    const workWeeks = getWorkingWeeks();
    const daysPerWeek = parseInt(formData.daysPerWeek) || 5;
    const weeksPerMonth = workWeeks / 12;
    const federalTaxRate = (formData.federalTaxRate || 0) / 100;
    const totalTaxRate = SELF_EMPLOYMENT_TAX + federalTaxRate;
    
    // Calculate months remaining after phase 1 completes (within 12-month year)
    const monthsInPhase2 = 12 - phase1.endMonth;
    const weeksInPhase2 = monthsInPhase2 * weeksPerMonth;
    
    // Get savings goal info
    const savingsGoal = parseFloat(formData.savingsGoal) || 0;
    const savingsWeekly = phase2.activeGoals.find(g => g.id === 'savings')?.weeklyAmount || 0;
    
    // Savings accumulated during Phase 1
    const savingsGoalFromPhase1 = phase1.activeGoals?.find(g => g.id === 'savings');
    const savingsFromPhase1 = savingsGoalFromPhase1 ? savingsGoalFromPhase1.weeklyAmount * (phase1.endMonth * weeksPerMonth) : 0;
    
    // OPTION A: Keep Phase 1 target - extra savings by year end
    const extraPerWeekOptionA = phase1.netWeekly - phase2.netWeekly;
    const extraSavingsByYearEnd = extraPerWeekOptionA * weeksInPhase2;
    const totalSavedOptionA = savingsGoal + extraSavingsByYearEnd;
    
    // OPTION C: Bare Minimum - Phase 2's natural target, meets savings goal exactly
    const totalSavedOptionC = savingsGoal;
    
    // OPTION B: Flexibility - a middle ground between A and C
    // Calculate what happens if they take a day off at Phase 1 daily rate
    const reducedDays = Math.max(daysPerWeek - 1, 3);
    const flexWeeklyAtReducedDays = phase1.grossDaily * reducedDays;
    const flexNetWeeklyAtReducedDays = flexWeeklyAtReducedDays * (1 - totalTaxRate);
    const baseExpensesWeekly = (parseFloat(formData.monthlyExpenses) * 12) / workWeeks;
    const flexSavingsWeeklyAtReducedDays = flexNetWeeklyAtReducedDays - baseExpensesWeekly;
    const flexTotalSavingsPhase2 = flexSavingsWeeklyAtReducedDays > 0 ? flexSavingsWeeklyAtReducedDays * weeksInPhase2 : 0;
    const totalSavedIfDayOff = savingsFromPhase1 + flexTotalSavingsPhase2;
    
    // Check if taking a day off can still meet the savings goal
    const canTakeDayOff = totalSavedIfDayOff >= savingsGoal && daysPerWeek > 3;
    
    // If they CAN take a day off and still meet goal, that's Path B
    // If they CAN'T, then Path B is working same days but slightly less per day (a buffer above bare minimum)
    // Calculate a middle ground: halfway between Phase 1 and Phase 2 weekly targets
    const midpointWeekly = (phase1.grossWeekly + phase2.grossWeekly) / 2;
    const midpointDaily = midpointWeekly / daysPerWeek;
    const midpointNetWeekly = midpointWeekly * (1 - totalTaxRate);
    const midpointSavingsWeekly = midpointNetWeekly - baseExpensesWeekly;
    const totalSavedAtMidpoint = savingsFromPhase1 + midpointSavingsWeekly * weeksInPhase2;
    
    // Choose which flexibility option to show
    let flexibilityOption;
    if (canTakeDayOff) {
      // They can take a day off and still meet their goal
      flexibilityOption = {
        type: 'day-off',
        dailyTarget: phase1.grossDaily,
        daysPerWeek: reducedDays,
        weeklyTarget: flexWeeklyAtReducedDays,
        totalSavedByYearEnd: totalSavedIfDayOff,
        description: `Same daily rate, ${daysPerWeek - reducedDays} day off`
      };
    } else {
      // They need to work same days but can ease up on daily target
      flexibilityOption = {
        type: 'lower-daily',
        dailyTarget: midpointDaily,
        daysPerWeek: daysPerWeek,
        weeklyTarget: midpointWeekly,
        totalSavedByYearEnd: totalSavedAtMidpoint,
        description: 'Lower daily target, same days'
      };
    }
    
    // OPTION D: Buy Back Time - keep Phase 1 target, route the extra to the time-off fund
    const netWeeklyNeed = calculateNetWeeklyTarget();
    const extraWeeksOffFunded = netWeeklyNeed > 0 ? extraSavingsByYearEnd / netWeeklyNeed : 0;
    
    return {
      phase1,
      phase2,
      monthsInPhase2,
      weeksInPhase2,
      savingsGoal,
      daysPerWeek,
      // Option A: Keep pushing - exceed savings goal
      optionA: {
        name: "Keep Your Momentum",
        weeklyTarget: phase1.grossWeekly,
        dailyTarget: phase1.grossDaily,
        daysPerWeek: daysPerWeek,
        extraSavings: extraSavingsByYearEnd,
        totalSavedByYearEnd: totalSavedOptionA,
        extraPerWeek: extraPerWeekOptionA
      },
      // Option B: Flexibility - a RANGE between A and C
      optionB: {
        name: "Flexibility",
        // Show the range they can choose from
        weeklyTargetHigh: phase1.grossWeekly,
        weeklyTargetLow: phase2.grossWeekly,
        dailyTargetHigh: phase1.grossDaily,
        dailyTargetLow: phase2.grossDaily,
        // Savings range
        totalSavedHigh: totalSavedOptionA,
        totalSavedLow: savingsGoal,
        extraSavingsHigh: extraSavingsByYearEnd,
        daysPerWeek: daysPerWeek
      },
      // Option C: Cover Your Expenses - just meet savings goal exactly
      optionC: {
        name: "Cover Your Expenses",
        weeklyTarget: phase2.grossWeekly,
        dailyTarget: phase2.grossDaily,
        daysPerWeek: daysPerWeek,
        totalSavedByYearEnd: totalSavedOptionC
      },
      // Option D: Buy Back Time - keep original target, extra funds time off
      optionD: {
        name: "Buy Back Time",
        weeklyTarget: phase1.grossWeekly,
        dailyTarget: phase1.grossDaily,
        daysPerWeek: daysPerWeek,
        extraPerWeek: extraPerWeekOptionA,
        ptoFundByYearEnd: extraSavingsByYearEnd,
        extraWeeksOff: extraWeeksOffFunded,
        totalSavedByYearEnd: savingsGoal
      }
    };
  };

  // Determine which trajectory story to show
  const getTrajectoryStory = () => {
    const hasDebt = formData.debtAmount && parseFloat(formData.debtAmount) > 0;
    const hasSavings = formData.savingsGoal && parseFloat(formData.savingsGoal) > 0;
    
    // Check for debt and savings combinations first (these take priority)
    if (hasDebt && hasSavings) return 'debt-and-savings'; // Full 3-phase story
    if (hasDebt && !hasSavings) return 'debt-only'; // 2 chapters: payoff → possibilities
    if (!hasDebt && hasSavings) return 'savings-only'; // Accumulation over year
    
    // If user has a specific target in mind (and no debt/savings), show target mode
    if (formData.hasTargetInMind && formData.monthlyTarget) {
      return 'target-mode';
    }
    
    return 'expenses-only'; // Steady rhythm
  };

  // Check if timeline feature should be shown (multiple goals with different timelines)
  const shouldShowTimeline = () => {
    const timelines = getGoalTimelines();
    if (timelines.length === 0) return false;
    // Show if there's at least one goal OR if multiple goals have different end dates
    if (timelines.length === 1) return true; // Show "what if you finish early" even for single goal
    const uniqueEndMonths = new Set(timelines.map(t => t.endMonth));
    return uniqueEndMonths.size >= 1;
  };

  const togglePath = (pathId) => {
    if (selectedPaths.includes(pathId)) {
      setSelectedPaths(selectedPaths.filter(p => p !== pathId));
    } else if (selectedPaths.length < 3) {
      setSelectedPaths([...selectedPaths, pathId]);
    }
  };

  // Determine which step we're on - NEW FLOW: Everyone answers the same questions
  const getSteps = () => {
    // Fixed flow: expenses → debt → savings → weeks → days → taxes → buffer (expenses path only)
    if (formData.hasTargetInMind) {
      return ['expenses', 'debt', 'savings', 'weeks', 'days', 'taxes'];
    }
    return ['expenses', 'debt', 'savings', 'weeks', 'days', 'taxes', 'buffer'];
  };

  const steps = getSteps();
  const currentStepId = steps[currentStep];

  const canProceed = () => {
    switch(currentStepId) {
      case 'expenses':
        // First, user must select a path
        if (!formData.entryPathSelected) return false;
        // Then, accept either monthly expenses OR a specific monthly target
        if (formData.hasTargetInMind) {
          return formData.monthlyTarget && parseFloat(formData.monthlyTarget) > 0;
        }
        return formData.monthlyExpenses && parseFloat(formData.monthlyExpenses) > 0;
      case 'debt':
        // Debt is optional - can proceed with $0 or any amount with timeline
        if (!formData.debtAmount || parseFloat(formData.debtAmount) === 0) return true;
        return formData.debtMonths && parseInt(formData.debtMonths) > 0;
      case 'savings':
        // Savings is optional - can proceed with $0 or any amount
        return true; // Always valid, will auto-set savingsMonths to 12 if amount > 0
      case 'weeks':
        if (formData.weeksOff === 'custom') {
          const customWeeksOff = parseInt(formData.customWeeksOff);
          return customWeeksOff >= 0 && customWeeksOff <= 51;
        }
        return true;
      case 'days':
        return formData.daysPerWeek && parseInt(formData.daysPerWeek) > 0 && parseInt(formData.daysPerWeek) <= 7;
      case 'taxes':
        return true; // Always valid, has default
      case 'buffer':
        return true; // Always valid, has default
      default:
        return true;
    }
  };

  const handleNext = () => {
    // Auto-populate selectedPaths based on inputs when moving to results
    if (currentStep === steps.length - 1) {
      // Build selectedPaths from actual inputs
      const paths = ['income']; // Income stability is always on
      if (formData.debtAmount && parseFloat(formData.debtAmount) > 0) {
        paths.push('debt');
      }
      if (formData.savingsGoal && parseFloat(formData.savingsGoal) > 0) {
        paths.push('savings');
      }
      setSelectedPaths(paths);
      
      // Track completion
      trackEvent('complete', {
        selected_paths: paths,
        monthly_expenses: parseFloat(formData.monthlyExpenses) || 0,
        weekly_target: calculateRelayTarget(),
        daily_target: calculateDailyTarget(),
        days_per_week: parseInt(formData.daysPerWeek) || 5,
        working_weeks: getWorkingWeeks()
      });
      setShowResults(true);
      // Scroll to results with offset so the big number isn't cut off
      setTimeout(() => {
        const resultsEl = document.getElementById('results-section');
        if (resultsEl) {
          const yOffset = -120; // Negative offset to show more content above
          const y = resultsEl.getBoundingClientRect().top + window.pageYOffset + yOffset;
          window.scrollTo({ top: y, behavior: 'smooth' });
        }
      }, 100);
    } else {
      // Track start when moving from first step
      if (currentStep === 0) {
        trackEvent('start', { flow: 'diagnostic' });
      }
      setCurrentStep(prev => prev + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
    }
  };

  const resetCalculator = () => {
    setCurrentStep(0);
    setSelectedPaths([]);
    setFormData({
      monthlyExpenses: '',
      hasTargetInMind: false,
      entryPathSelected: false,
      monthlyTarget: '',
      debtAmount: '',
      debtMonths: '',
      savingsGoal: '',
      savingsMonths: '',
      weeksOff: 4,
      customWeeksOff: '',
      daysPerWeek: 5,
      federalTaxRate: 10
    });
    setShowResults(false);
    setShowSharePreview(false);
    setEmailSent(false);
    setEmailInput('');
    setSanityCheckExpenses(''); // Reset sanity check input
  };

  const downloadPDF = async () => {
    if (!placardRef.current) return;
    
    try {
      toast.loading('Creating your goal card...');
      trackEvent('download', { selected_paths: selectedPaths, weekly_target: calculateRelayTarget() });
      
      const canvas = await html2canvas(placardRef.current, {
        scale: 2,
        backgroundColor: '#FDF8F3',
        logging: false
      });
      
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF({
        orientation: placardFormat === 'landscape' ? 'landscape' : 'portrait',
        unit: 'px',
        format: [canvas.width / 2, canvas.height / 2]
      });
      
      pdf.addImage(imgData, 'PNG', 0, 0, canvas.width / 2, canvas.height / 2);
      pdf.save(`my-income-target-${placardFormat}-${Date.now()}.pdf`);
      toast.dismiss();
      toast.success('Your goal card is ready!');
    } catch (error) {
      toast.dismiss();
      toast.error('Something went wrong. Try again.');
    }
  };

  const downloadPNG = async () => {
    if (!placardRef.current) return;
    
    try {
      toast.loading('Creating your image...');
      trackEvent('download', { selected_paths: selectedPaths, weekly_target: calculateRelayTarget() });
      
      const canvas = await html2canvas(placardRef.current, {
        scale: 2,
        backgroundColor: '#FDF8F3',
        logging: false
      });
      
      const link = document.createElement('a');
      link.download = `my-income-target-${placardFormat}-${Date.now()}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
      
      toast.dismiss();
      toast.success('Image saved!');
    } catch (error) {
      toast.dismiss();
      toast.error('Something went wrong. Try again.');
    }
  };

  // Copy goal statement to clipboard with rich text formatting
  const copyGoalToClipboard = async () => {
    const plainText = getGoalStatement();
    const htmlText = getGoalStatementHTML();
    
    try {
      // Try to copy as rich text (HTML) for apps that support it
      await navigator.clipboard.write([
        new ClipboardItem({
          'text/plain': new Blob([plainText], { type: 'text/plain' }),
          'text/html': new Blob([htmlText], { type: 'text/html' })
        })
      ]);
    } catch (err) {
      // Fallback to plain text if rich text copy fails
      await navigator.clipboard.writeText(plainText);
    }
    
    setGoalCopied(true);
    toast.success('Goal copied to clipboard!');
    trackEvent('copy_goal', { selected_paths: selectedPaths, weekly_target: calculateRelayTarget() });
    setTimeout(() => setGoalCopied(false), 3000);
  };

  // HTML version of goal statement for rich text clipboard copy
  const getGoalStatementHTML = () => {
    const target = calculateRelayTarget();
    const workWeeks = getWorkingWeeks();
    
    // Build parts with bold numbers
    let goalParts = [];
    
    if (formData.hasTargetInMind && formData.monthlyTarget) {
      goalParts.push(`hit my <b>${formatGoalCurrency(parseFloat(formData.monthlyTarget))}/month</b> income target`);
    } else {
      goalParts.push('cover my monthly expenses');
    }
    
    if (selectedPaths.includes('debt') && formData.debtAmount && formData.debtMonths) {
      const months = parseInt(formData.debtMonths);
      const timeframe = months === 12 ? 'in 1 year' : `in ${months} months`;
      goalParts.push(`pay off <b>${formatGoalCurrency(parseFloat(formData.debtAmount))}</b> ${timeframe}`);
    }
    
    if (selectedPaths.includes('savings') && formData.savingsGoal && formData.savingsMonths) {
      const months = parseInt(formData.savingsMonths);
      const timeframe = months === 12 ? 'in 1 year' : `in ${months} months`;
      goalParts.push(`save <b>${formatGoalCurrency(parseFloat(formData.savingsGoal))}</b> ${timeframe}`);
    }
    
    if (selectedPaths.includes('time') && getWeeksOff() > 0) {
      goalParts.push(`take <b>${getWeeksOff()} weeks</b> off this year`);
    }
    
    // Build the sentence
    
    goalParts = mergeYearGoals(goalParts);
    
    
    let partsString;
    if (goalParts.length === 1) {
      partsString = goalParts[0];
    } else if (goalParts.length === 2) {
      partsString = `${goalParts[0]} and ${goalParts[1]}`;
    } else {
      const lastPart = goalParts.pop();
      partsString = `${goalParts.join(', ')}, and ${lastPart}`;
    }
    
    return `I need to earn <b>${formatGoalCurrency(target)}</b> every week for <b>${workWeeks} weeks</b> so I can ${partsString}.`;
  };

  const getShareText = () => {
    const weeklyTarget = formatCurrency(calculateRelayTarget());
    const dailyTarget = formatCurrency(calculateDailyTarget());
    const daysPerWeek = parseInt(formData.daysPerWeek) || 5;
    const workWeeks = getWorkingWeeks();
    const weeksOff = getWeeksOff();
    
    // Three caption scenarios based on user's situation
    // Each sentence on its own line for better readability
    
    // 1. Time-off story (weeksOff > 0)
    if (weeksOff > 0) {
      return `This year I'm taking ${weeksOff} week${weeksOff !== 1 ? 's' : ''} off.

Payday Relay helped me figure out my weekly income target so I can still reach my financial goals.

Mine: ${weeklyTarget}/week
(${dailyTarget}/day × ${daysPerWeek} days)
for ${workWeeks} weeks

1099 workers, do you know yours?

Discover your weekly income target:
https://vr9p10f.short.gy/my-link

#PaydayRelay`;
    }
    
    // 2. Side hustle story (daysPerWeek <= 2)
    if (daysPerWeek <= 2) {
      return `I do 1099 work ${daysPerWeek} day${daysPerWeek !== 1 ? 's' : ''} a week.

Payday Relay helped me figure out the weekly target I need to hit to reach my goals.

Mine: ${weeklyTarget}/week
(${dailyTarget}/day × ${daysPerWeek} day${daysPerWeek !== 1 ? 's' : ''})

Discover your weekly income target:
https://vr9p10f.short.gy/my-link

#PaydayRelay`;
    }
    
    // 3. General clarity story (default)
    return `Payday Relay just helped me calculate my weekly income target.

That's the weekly number I need to hit to reach my financial goals.

Mine: ${weeklyTarget}/week
(${dailyTarget}/day × ${daysPerWeek} days)

1099 workers, do you know yours?

Discover your weekly income target:
https://vr9p10f.short.gy/my-link

#PaydayRelay`;
  };

  // Generate placard image as blob for sharing
  const generatePlacardBlob = async () => {
    if (!placardRef.current) return null;
    try {
      const canvas = await html2canvas(placardRef.current, {
        scale: 2,
        useCORS: true,
        allowTaint: true,
        backgroundColor: '#FFFFFF'
      });
      return new Promise((resolve) => {
        canvas.toBlob((blob) => resolve(blob), 'image/png', 1.0);
      });
    } catch (error) {
      // Placard generation failed - return null to trigger fallback
      return null;
    }
  };

  // Download placard image
  const downloadPlacardForShare = async () => {
    const blob = await generatePlacardBlob();
    if (blob) {
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'my-income-target.png';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      return true;
    }
    return false;
  };

  const shareOnFacebook = async () => {
    trackEvent('share', { selected_paths: selectedPaths, weekly_target: calculateRelayTarget() });
    
    // Try Web Share API with image
    const blob = await generatePlacardBlob();
    if (blob && navigator.canShare) {
      const file = new File([blob], 'my-income-target.png', { type: 'image/png' });
      if (navigator.canShare({ files: [file] })) {
        try {
          await navigator.share({
            files: [file],
            text: getShareText()
          });
          return;
        } catch (e) {
          if (e.name !== 'AbortError') {
            // Fall through to alternative
          } else {
            return; // User cancelled
          }
        }
      }
    }
    
    // Fallback: download image and open Facebook
    await downloadPlacardForShare();
    navigator.clipboard.writeText(getShareText());
    toast.success('Image downloaded & caption copied! Upload to Facebook.');
    const url = encodeURIComponent('https://vr9p10f.short.gy/my-link');
    window.open(`https://www.facebook.com/sharer/sharer.php?u=${url}`, '_blank');
  };

  const shareOnInstagram = async () => {
    trackEvent('share', { selected_paths: selectedPaths, weekly_target: calculateRelayTarget() });
    
    // Try Web Share API with image
    const blob = await generatePlacardBlob();
    if (blob && navigator.canShare) {
      const file = new File([blob], 'my-income-target.png', { type: 'image/png' });
      if (navigator.canShare({ files: [file] })) {
        try {
          await navigator.share({
            files: [file],
            text: getShareText()
          });
          return;
        } catch (e) {
          if (e.name !== 'AbortError') {
            // Fall through to alternative
          } else {
            return;
          }
        }
      }
    }
    
    // Fallback: download image and copy caption
    await downloadPlacardForShare();
    navigator.clipboard.writeText(getShareText());
    toast.success('Image downloaded & caption copied! Open Instagram to share.');
  };

  const shareOnTikTok = async () => {
    trackEvent('share', { selected_paths: selectedPaths, weekly_target: calculateRelayTarget() });
    
    // Try Web Share API with image
    const blob = await generatePlacardBlob();
    if (blob && navigator.canShare) {
      const file = new File([blob], 'my-income-target.png', { type: 'image/png' });
      if (navigator.canShare({ files: [file] })) {
        try {
          await navigator.share({
            files: [file],
            text: getShareText()
          });
          return;
        } catch (e) {
          if (e.name !== 'AbortError') {
            // Fall through to alternative
          } else {
            return;
          }
        }
      }
    }
    
    // Fallback: download image and copy caption
    await downloadPlacardForShare();
    navigator.clipboard.writeText(getShareText());
    toast.success('Image downloaded & caption copied! Open TikTok to share.');
  };

  const shareOnThreads = async () => {
    trackEvent('share', { selected_paths: selectedPaths, weekly_target: calculateRelayTarget() });
    
    // Try Web Share API with image
    const blob = await generatePlacardBlob();
    if (blob && navigator.canShare) {
      const file = new File([blob], 'my-income-target.png', { type: 'image/png' });
      if (navigator.canShare({ files: [file] })) {
        try {
          await navigator.share({
            files: [file],
            text: getShareText()
          });
          return;
        } catch (e) {
          if (e.name !== 'AbortError') {
            // Fall through to alternative
          } else {
            return;
          }
        }
      }
    }
    
    // Fallback: download image and open Threads
    await downloadPlacardForShare();
    const text = encodeURIComponent(getShareText());
    window.open(`https://www.threads.net/intent/post?text=${text}`, '_blank');
    toast.success('Image downloaded! Upload it to your Threads post.');
  };

  const copyToClipboard = async () => {
    try {
      trackEvent('share', { selected_paths: selectedPaths, weekly_target: calculateRelayTarget() });
      
      // Try to copy image to clipboard (works on some browsers)
      const blob = await generatePlacardBlob();
      if (blob) {
        try {
          await navigator.clipboard.write([
            new ClipboardItem({
              'image/png': blob,
              'text/plain': new Blob([getShareText()], { type: 'text/plain' })
            })
          ]);
          setCopied(true);
          toast.success('Image & caption copied to clipboard!');
          setTimeout(() => setCopied(false), 2000);
          return;
        } catch (e) {
          // ClipboardItem API not supported, fall back to text-only copy
          console.warn('ClipboardItem not supported, using fallback:', e.message);
        }
      }
      
      // Fallback: download image and copy text
      await downloadPlacardForShare();
      await navigator.clipboard.writeText(getShareText());
      setCopied(true);
      toast.success('Image downloaded & caption copied!');
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      toast.error('Failed to copy');
    }
  };

  // Email subscription handler
  const handleEmailSubscribe = async (e) => {
    e.preventDefault();
    if (!emailInput || emailSending) return;
    
    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(emailInput)) {
      toast.error('Please enter a valid email address');
      return;
    }
    
    setEmailSending(true);
    
    try {
      // No duplicate check - each payment allows one download
      const trajectoryType = getTrajectoryStory();
      const taxRate = parseFloat(formData.federalTaxRate) || 0;
      const grossWeekly = calculateRelayTarget();
      const netWeekly = grossWeekly * (1 - (SELF_EMPLOYMENT_TAX + taxRate / 100));
      const monthlyTarget = formData.hasTargetInMind ? parseFloat(formData.monthlyTarget) : null;
      const feasibility = getFeasibilityCheck();
      const trajectoryOptions = calculateMomentumOptions();
      
      const response = await fetch(`${API_URL}/api/email/subscribe`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: emailInput.toLowerCase(),
          weekly_target: grossWeekly,
          daily_target: calculateDailyTarget(),
          days_per_week: parseInt(formData.daysPerWeek) || 5,
          working_weeks: getWorkingWeeks(),
          goal_statement: getGoalStatement(),
          trajectory_type: trajectoryType,
          debt_amount: parseFloat(formData.debtAmount) || null,
          debt_months: parseInt(formData.debtMonths) || null,
          savings_goal: parseFloat(formData.savingsGoal) || null,
          savings_months: parseInt(formData.savingsMonths) || null,
          monthly_expenses: parseFloat(formData.monthlyExpenses) || 0,
          time_off_buffer: getTimeOffBuffer(),
          weeks_off: getWeeksOff(),
          federal_tax_rate: taxRate,
          net_weekly: netWeekly,
          monthly_target: monthlyTarget,
          gross_annual_income: grossWeekly * getWorkingWeeks(),
          // SMART feasibility check data
          feasibility_status: feasibility.status,
          feasibility_message: feasibility.message,
          max_monthly_expenses: feasibility.maxMonthlyExpenses || null,
          has_target_in_mind: formData.hasTargetInMind,
          sanity_check_expenses: parseFloat(sanityCheckExpenses) || null,
          // Trajectory options for PDF
          trajectory_options: trajectoryOptions
        })
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        if (response.status === 400 && errorData.detail) {
          toast.error(errorData.detail);
          return;
        }
        throw new Error('Failed to send report');
      }
      
      const result = await response.json();
      
      if (result.status === 'success') {
        setEmailSent(true);
        sessionStorage.setItem('email_sent', 'true');
        setHasDownloadedPDF(true);
        sessionStorage.setItem('downloaded_pdf', 'true');
        trackEvent('email_subscribe', { weekly_target: calculateRelayTarget() });
        
        // Track Meta Pixel Lead event
        if (typeof window.fbq === 'function') {
          window.fbq('track', 'Lead', {
            content_name: 'Income Mapping Results',
            value: 4.95,
            currency: 'USD'
          });
        }
        
        toast.success('Your personalized report is on its way!');
        
        // Show Victory Lap takeover (user drives when to see the offer)
        setTimeout(() => {
          setShowVictoryLap(true);
        }, 800);
      } else if (result.status === 'saved') {
        toast.error("We saved your numbers, but the email didn't go through. Please try again in a minute, if it keeps failing, email support@paydayrelay.com.");
      } else {
        toast.error('Something went wrong. Please try again.');
      }
    } catch (err) {
      toast.error('Failed to send. Please try again.');
    } finally {
      setEmailSending(false);
    }
  };

  // Handle PDF download (one-time per purchase)
  const handleDownloadPDF = async () => {
    if (hasDownloadedPDF) {
      toast.error('You\'ve already downloaded your plan for this session. When your numbers change, come back for a new session.');
      return;
    }
    
    if (!emailInput) {
      toast.error('Please enter your email to receive your Income Map Results.');
      return;
    }
    
    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(emailInput)) {
      toast.error('Please enter a valid email address');
      return;
    }
    
    setIsDownloadingPDF(true);
    
    try {
      // No duplicate check - each payment allows one download
      const trajectoryType = getTrajectoryStory();
      const taxRate = parseFloat(formData.federalTaxRate) || 0;
      const grossWeekly = calculateRelayTarget();
      const netWeekly = grossWeekly * (1 - (SELF_EMPLOYMENT_TAX + taxRate / 100));
      const monthlyTarget = formData.hasTargetInMind ? parseFloat(formData.monthlyTarget) : null;
      const feasibility = getFeasibilityCheck();
      const trajectoryOptions = calculateMomentumOptions();
      
      const response = await fetch(`${API_URL}/api/email/subscribe`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: emailInput.toLowerCase(),
          weekly_target: grossWeekly,
          daily_target: calculateDailyTarget(),
          days_per_week: parseInt(formData.daysPerWeek) || 5,
          working_weeks: getWorkingWeeks(),
          goal_statement: getGoalStatement(),
          trajectory_type: trajectoryType,
          debt_amount: parseFloat(formData.debtAmount) || null,
          debt_months: parseInt(formData.debtMonths) || null,
          savings_goal: parseFloat(formData.savingsGoal) || null,
          savings_months: parseInt(formData.savingsMonths) || null,
          monthly_expenses: parseFloat(formData.monthlyExpenses) || 0,
          time_off_buffer: getTimeOffBuffer(),
          weeks_off: getWeeksOff(),
          federal_tax_rate: taxRate,
          net_weekly: netWeekly,
          monthly_target: monthlyTarget,
          gross_annual_income: grossWeekly * getWorkingWeeks(),
          feasibility_status: feasibility.status,
          feasibility_message: feasibility.message,
          max_monthly_expenses: feasibility.maxMonthlyExpenses || null,
          has_target_in_mind: formData.hasTargetInMind,
          sanity_check_expenses: parseFloat(sanityCheckExpenses) || null,
          trajectory_options: trajectoryOptions
        })
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        if (response.status === 400 && errorData.detail) {
          toast.error(errorData.detail);
          return;
        }
        throw new Error('Failed to send report');
      }
      
      const result = await response.json();
      
      if (result.status === 'success') {
        rememberUser(emailInput);
        setEmailSent(true);
        sessionStorage.setItem('email_sent', 'true');
        setHasDownloadedPDF(true);
        sessionStorage.setItem('downloaded_pdf', 'true');
        trackEvent('pdf_download', { weekly_target: calculateRelayTarget() });
        toast.success('Your Income Mapping Results have been sent to your email!');
        
        // Show Victory Lap takeover
        setTimeout(() => {
          setShowVictoryLap(true);
        }, 800);
      } else if (result.status === 'saved') {
        toast.error("We saved your numbers, but the email didn't go through. Please try again in a minute, if it keeps failing, email support@paydayrelay.com.");
      } else {
        toast.error('Something went wrong. Please try again.');
      }
    } catch (err) {
      toast.error('Failed to generate your map. Please try again.');
    } finally {
      setIsDownloadingPDF(false);
    }
  };

  return (
    <div className="min-h-screen" style={{ background: '#FDFBF7' }}>

      {/* Completion Screen - shown after upsell flow is complete */}
      {upsellFlowComplete && <CompletionScreen />}

      {/* Main Calculator Content - hidden after upsell flow complete */}
      {!upsellFlowComplete && (
      <>
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 px-4 sm:px-6 lg:px-8 py-3" style={{ background: 'rgba(253, 251, 247, 0.95)', backdropFilter: 'blur(8px)' }}>
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <Link
            to="/"
            className="flex items-center transition-opacity hover:opacity-80"
            data-testid="income-map-nav-logo"
          >
            <img
              src="/images/paydayrelaylogo.png"
              alt="Payday Relay"
              className="h-20 sm:h-32 lg:h-40 w-auto"
            />
            <BetaBadge />
          </Link>
        </div>
      </nav>

      {/* Free Income Map hero, Step 1 of every Payday Relay race */}
      <section className="pt-32 sm:pt-44 lg:pt-52 pb-4 px-4 text-center" data-testid="income-map-free-hero">
        <p className="text-xs font-bold uppercase tracking-[0.25em] mb-3" style={{ color: '#8B7E6A' }}>
          Step 1 of your race · free
        </p>
        <h1 className="text-4xl sm:text-5xl lg:text-6xl mb-4" style={{ fontFamily: "'Fraunces', Georgia, serif", color: '#2A1E5C' }}>
          Map Your Number
        </h1>
        <p className="text-base sm:text-lg max-w-xl mx-auto leading-relaxed" style={{ color: '#5D5A6B' }}>
          Turn your bills, goals, taxes, and time off into one clear weekly income target.
          Free, it's the first leg of every Payday Relay race.
        </p>
      </section>

      {/* Calculator Section - Shows paywall or calculator based on payment status */}
      <section id="calculator" className="py-6 lg:py-8 relative z-10">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">

          {/* Paywall Check */}
          {isCheckingPayment ? (
            <div className="text-center py-8">
              <Loader2 className="w-12 h-12 mx-auto mb-4 animate-spin" style={{ color: '#F5CB5C' }} />
              <p className="font-heading text-xl" style={{ color: '#2A1E5C' }}>Verifying your access...</p>
            </div>
          ) : !hasPaidAccess ? (
            /* Hidden - users click the button above which triggers payment */
            <div className="hidden"></div>
          ) : !showResults ? (
            <div className="card-organic p-8 lg:p-12 shadow-dreamy" data-testid="calculator">
              {/* Progress */}
              <div className="mb-10">
                <div className="flex justify-between mb-4">
                  {steps.map((step, idx) => (
                    <div 
                      key={step}
                      className={`w-3 h-3 rounded-full transition-all ${
                        idx < currentStep ? 'bg-teal-500' : 
                        idx === currentStep ? 'bg-yellow-400 scale-125 shadow-lg' : 
                        'bg-gray-200'
                      }`}
                    />
                  ))}
                </div>
                <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                  <div 
                    className="h-full rounded-full transition-all duration-500"
                    style={{ 
                      width: `${(currentStep / (steps.length - 1)) * 100}%`,
                      background: 'linear-gradient(90deg, #F5CB5C, #2E86AB)'
                    }}
                  />
                </div>
              </div>

              {/* Step Content */}
              <div className="min-h-[400px] flex flex-col">

                {/* Step: Entry Path Selection & Input */}
                {currentStepId === 'expenses' && (
                  <div className="flex-1 flex flex-col items-center justify-center animate-fade-up-soft">
                    
                    {/* Path Selection - Two Equal Options */}
                    {!formData.entryPathSelected ? (
                      <>
                        <div className="w-20 h-20 blob-1 flex items-center justify-center mb-6" style={{ background: 'rgba(245, 203, 92, 0.15)' }}>
                          <Target className="w-10 h-10" style={{ color: '#F5CB5C' }} />
                        </div>
                        <h3 className="font-heading text-2xl font-medium mb-2 text-center" style={{ color: '#2A1E5C' }}>
                          How do you want to calculate your weekly income target?
                        </h3>
                        <p className="font-body text-sm mb-8 text-center max-w-md" style={{ color: '#8B7E6A' }}>
                          Choose the starting point that fits your situation.
                        </p>
                        
                        <div className="w-full max-w-lg space-y-4">
                          {/* Option 1: Target First */}
                          <button
                            onClick={() => setFormData({ ...formData, hasTargetInMind: true, entryPathSelected: true })}
                            className="w-full p-5 rounded-xl text-left transition-all hover:scale-[1.02] group"
                            style={{ 
                              background: 'rgba(245, 203, 92, 0.08)', 
                              border: '2px solid rgba(245, 203, 92, 0.5)'
                            }}
                            data-testid="path-target"
                          >
                            <div className="flex items-start gap-4">
                              <div className="flex-shrink-0 w-12 h-12 rounded-full flex items-center justify-center" style={{ background: 'rgba(245, 203, 92, 0.2)' }}>
                                <Target className="w-6 h-6" style={{ color: '#F5CB5C' }} />
                              </div>
                              <div className="flex-1">
                                <h4 className="font-heading text-lg font-medium mb-1" style={{ color: '#2A1E5C' }}>
                                  Start with a monthly take-home target
                                </h4>
                                <p className="font-body text-sm mb-2" style={{ color: '#8B7E6A' }}>
                                  I already know how much I want to take home each month.
                                </p>
                                <p className="font-body text-xs italic mb-3" style={{ color: '#2A1E5C' }}>
                                  Best if you're aiming high or have a specific goal in mind.
                                </p>
                                <div className="flex flex-wrap gap-2">
                                  <span className="px-2 py-1 rounded-full font-body text-xs" style={{ background: 'rgba(245, 203, 92, 0.15)', color: '#8B7E6A' }}>Visa requirements</span>
                                  <span className="px-2 py-1 rounded-full font-body text-xs" style={{ background: 'rgba(245, 203, 92, 0.15)', color: '#8B7E6A' }}>Rent qualification</span>
                                  <span className="px-2 py-1 rounded-full font-body text-xs" style={{ background: 'rgba(245, 203, 92, 0.15)', color: '#8B7E6A' }}>Business goals</span>
                                </div>
                              </div>
                              <ChevronRight className="w-5 h-5 flex-shrink-0 opacity-50 group-hover:opacity-100 transition-opacity" style={{ color: '#F5CB5C' }} />
                            </div>
                          </button>
                          
                          {/* Option 2: Expenses First */}
                          <button
                            onClick={() => setFormData({ ...formData, hasTargetInMind: false, entryPathSelected: true })}
                            className="w-full p-5 rounded-xl text-left transition-all hover:scale-[1.02] group"
                            style={{ 
                              background: 'rgba(46, 134, 171, 0.08)', 
                              border: '2px solid rgba(46, 134, 171, 0.5)'
                            }}
                            data-testid="path-expenses"
                          >
                            <div className="flex items-start gap-4">
                              <div className="flex-shrink-0 w-12 h-12 rounded-full flex items-center justify-center" style={{ background: 'rgba(46, 134, 171, 0.2)' }}>
                                <DollarSign className="w-6 h-6" style={{ color: '#2E86AB' }} />
                              </div>
                              <div className="flex-1">
                                <h4 className="font-heading text-lg font-medium mb-1" style={{ color: '#2A1E5C' }}>
                                  Start with my monthly expenses
                                </h4>
                                <p className="font-body text-sm mb-2" style={{ color: '#8B7E6A' }}>
                                  Calculate the income I need based on my bills and goals.
                                </p>
                                <p className="font-body text-xs italic mb-3" style={{ color: '#2A1E5C' }}>
                                  Best if you want to know the minimum you need to cover your goals.
                                </p>
                                <div className="flex flex-wrap gap-2">
                                  <span className="px-2 py-1 rounded-full font-body text-xs" style={{ background: 'rgba(46, 134, 171, 0.15)', color: '#8B7E6A' }}>Rent & bills</span>
                                  <span className="px-2 py-1 rounded-full font-body text-xs" style={{ background: 'rgba(46, 134, 171, 0.15)', color: '#8B7E6A' }}>Debt payoff</span>
                                  <span className="px-2 py-1 rounded-full font-body text-xs" style={{ background: 'rgba(46, 134, 171, 0.15)', color: '#8B7E6A' }}>Savings goals</span>
                                </div>
                              </div>
                              <ChevronRight className="w-5 h-5 flex-shrink-0 opacity-50 group-hover:opacity-100 transition-opacity" style={{ color: '#2E86AB' }} />
                            </div>
                          </button>
                        </div>
                      </>
                    ) : formData.hasTargetInMind ? (
                      /* Target Input Mode */
                      <>
                        <div className="w-20 h-20 blob-1 flex items-center justify-center mb-6" style={{ background: 'rgba(245, 203, 92, 0.15)' }}>
                          <Target className="w-10 h-10" style={{ color: '#F5CB5C' }} />
                        </div>
                        <h3 className="font-heading text-2xl font-medium mb-2 text-center" style={{ color: '#2A1E5C' }}>
                          How much do you want to take home each month?
                        </h3>
                        <p className="font-body text-sm mb-6 text-center max-w-md" style={{ color: '#8B7E6A' }}>
                          Enter the take-home amount you want <strong>after taxes</strong>. We'll gross it up and translate it into your weekly target.
                        </p>
                        <div className="w-full max-w-xs mb-6 flex items-center justify-center gap-2">
                          <span className="font-heading text-2xl" style={{ color: '#C4B8A8' }}>$</span>
                          <input
                            type="number"
                            value={formData.monthlyTarget}
                            onChange={(e) => setFormData({ ...formData, monthlyTarget: e.target.value })}
                            placeholder="5,000"
                            step="500"
                            min="0"
                            className="w-40 font-heading text-3xl text-center py-4 border-b-3 bg-transparent focus:outline-none transition"
                            style={{ color: '#2A1E5C', borderBottomWidth: '3px', borderColor: '#E4DCD2' }}
                            data-testid="target-input"
                            autoFocus
                          />
                          <span className="font-body text-sm" style={{ color: '#A89F8F' }}>/month</span>
                        </div>
                        
                        {/* Examples */}
                        <div className="w-full max-w-sm p-4 rounded-xl mb-5" style={{ background: 'rgba(245, 203, 92, 0.08)' }}>
                          <p className="font-body text-xs font-medium mb-2" style={{ color: '#8B7E6A' }}>
                            Common reasons for a specific target:
                          </p>
                          <ul className="font-body text-xs space-y-1" style={{ color: '#A89F8F' }}>
                            <li>• Proof of income for apartment rental</li>
                            <li>• Residency requirements abroad</li>
                            <li>• Qualifying for a mortgage or loan</li>
                            <li>• Paying off a tax bill this year</li>
                            <li>• Meeting child support obligations</li>
                          </ul>
                        </div>
                        
                        {/* Switch path */}
                        <button
                          onClick={() => setFormData({ ...formData, hasTargetInMind: false, monthlyTarget: '', entryPathSelected: false })}
                          className="font-body text-sm underline decoration-dotted underline-offset-4 hover:opacity-80 transition"
                          style={{ color: '#8B7E6A' }}
                          data-testid="switch-path"
                        >
                          ← Choose a different starting point
                        </button>
                      </>
                    ) : (
                      /* Expenses Input Mode */
                      <>
                        <div className="w-20 h-20 blob-1 flex items-center justify-center mb-6" style={{ background: 'rgba(46, 134, 171, 0.15)' }}>
                          <DollarSign className="w-10 h-10" style={{ color: '#2E86AB' }} />
                        </div>
                        <h3 className="font-heading text-2xl font-medium mb-2 text-center" style={{ color: '#2A1E5C' }}>
                          What are your monthly expenses?
                        </h3>
                        <p className="font-body text-sm mb-8 text-center max-w-md" style={{ color: '#8B7E6A' }}>
                          Rent, bills, food, subscriptions. Everything you need to cover each month. 
                          An estimate is fine. This is your baseline.
                        </p>
                        <div className="w-full max-w-xs flex items-center justify-center gap-2">
                          <span className="font-heading text-2xl" style={{ color: '#C4B8A8' }}>$</span>
                          <input
                            type="number"
                            value={formData.monthlyExpenses}
                            onChange={(e) => setFormData({ ...formData, monthlyExpenses: e.target.value })}
                            placeholder="3,500"
                            step="500"
                            min="0"
                            className="w-40 font-heading text-3xl text-center py-4 border-b-3 bg-transparent focus:outline-none transition"
                            style={{ color: '#2A1E5C', borderBottomWidth: '3px', borderColor: '#E4DCD2' }}
                            data-testid="expenses-input"
                            autoFocus
                          />
                          <span className="font-body text-sm" style={{ color: '#A89F8F' }}>/month</span>
                        </div>
                        
                        {/* Switch path */}
                        <button
                          onClick={() => setFormData({ ...formData, hasTargetInMind: true, monthlyExpenses: '', entryPathSelected: false })}
                          className="mt-8 font-body text-sm underline decoration-dotted underline-offset-4 hover:opacity-80 transition"
                          style={{ color: '#8B7E6A' }}
                          data-testid="switch-path"
                        >
                          ← Choose a different starting point
                        </button>
                      </>
                    )}
                  </div>
                )}

                {/* Step: Debt - with timeline calculation */}
                {currentStepId === 'debt' && (
                  <div className="flex-1 flex flex-col items-center justify-center animate-fade-up-soft">
                    <div className="w-20 h-20 blob-3 flex items-center justify-center mb-6" style={{ background: 'rgba(222, 60, 75, 0.15)' }}>
                      <CreditCard className="w-10 h-10" style={{ color: '#DE3C4B' }} />
                    </div>
                    <h3 className="font-heading text-2xl font-medium mb-2 text-center" style={{ color: '#2A1E5C' }}>
                      What's your debt payoff goal for the next 12 months?
                    </h3>
                    <p className="font-body text-sm mb-8 text-center max-w-md" style={{ color: '#8B7E6A' }}>
                      Debt often extends beyond a year, but let's focus on what's achievable in the next 12 months.
                    </p>
                    <div className="w-full max-w-sm space-y-6">
                      <div className="relative">
                        <span className="absolute left-4 top-1/2 -translate-y-1/2 font-heading text-2xl" style={{ color: '#C4B8A8' }}>$</span>
                        <input
                          type="number"
                          value={formData.debtAmount}
                          onChange={(e) => setFormData({ ...formData, debtAmount: e.target.value })}
                          placeholder="5,000"
                          step="500"
                          min="0"
                          className="w-full font-heading text-3xl text-center pl-10 pr-4 py-4 border-b-3 bg-transparent focus:outline-none transition"
                          style={{ color: '#2A1E5C', borderBottomWidth: '3px', borderColor: '#E4DCD2' }}
                          data-testid="debt-input"
                          autoFocus
                        />
                      </div>
                      
                      {/* Only show timeline if they entered a debt amount */}
                      {formData.debtAmount && parseFloat(formData.debtAmount) > 0 && (
                        <div>
                          <label className="font-body text-sm block mb-3 text-center" style={{ color: '#8B7E6A' }}>
                            How many months to pay it off?
                          </label>
                          <div className="flex justify-center gap-2 flex-wrap">
                            {[3, 6, 9, 12].map((months) => (
                              <button
                                key={months}
                                onClick={() => setFormData({ ...formData, debtMonths: months.toString() })}
                                className={`px-4 py-2 rounded-full font-body text-sm transition-all ${
                                  formData.debtMonths === months.toString()
                                    ? 'bg-red-500 text-white'
                                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                }`}
                                data-testid={`debt-months-${months}`}
                              >
                                {months} months
                              </button>
                            ))}
                          </div>
                          <div className="mt-4">
                            <input
                              type="range"
                              min="1"
                              max="12"
                              value={formData.debtMonths || 6}
                              onChange={(e) => setFormData({ ...formData, debtMonths: e.target.value })}
                              className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-red-500"
                              data-testid="debt-months-slider"
                            />
                            <p className="font-body text-sm text-center mt-2" style={{ color: '#DE3C4B' }}>
                              {formData.debtMonths || 6} month{(formData.debtMonths || 6) !== '1' ? 's' : ''}
                            </p>
                          </div>
                          
                          {/* Target-path heads up: debt is carved OUT of the monthly target */}
                          {formData.hasTargetInMind && formData.monthlyTarget && (() => {
                            const months = parseInt(formData.debtMonths) || 6;
                            const debtAmt = parseFloat(formData.debtAmount) || 0;
                            const monthlyPayment = debtAmt / months;
                            const paymentAt12 = debtAmt / 12;
                            const targetNum = parseFloat(formData.monthlyTarget) || 0;
                            return (
                              <div className="mt-4 text-center p-4 rounded-xl" style={{ background: 'rgba(222, 60, 75, 0.08)', border: '1px solid rgba(222, 60, 75, 0.25)' }} data-testid="debt-target-heads-up">
                                <p className="font-body text-sm mb-1" style={{ color: '#DE3C4B' }}>
                                  {formatCurrency(debtAmt)} ÷ {months} months = <strong>{formatCurrency(monthlyPayment)}/month</strong> toward this debt
                                </p>
                                <p className="font-body text-sm" style={{ color: '#5D5A6B' }}>
                                  That comes <strong>out of</strong> your <strong style={{ color: '#2A1E5C' }}>{formatCurrency(targetNum)}/month</strong> target, we'll flag on the next screen if it doesn't leave enough for your expenses.
                                </p>
                                {months < 12 && (
                                  <button
                                    onClick={() => setFormData({ ...formData, debtMonths: '12' })}
                                    className="mt-3 px-4 py-2 rounded-full font-body text-sm transition-all hover:scale-[1.02]"
                                    style={{ background: 'rgba(76, 175, 80, 0.15)', color: '#2E7D32' }}
                                    data-testid="debt-stretch-suggestion"
                                  >
                                    Stretch it to 12 months and it's about <strong>{formatCurrency(paymentAt12)}/month</strong> instead →
                                  </button>
                                )}
                              </div>
                            );
                          })()}
                        </div>
                      )}
                      
                      {/* Skip option */}
                      <button
                        onClick={() => {
                          setFormData({ ...formData, debtAmount: '', debtMonths: '' });
                          setCurrentStep(prev => prev + 1);
                        }}
                        className="w-full py-3 font-body text-sm transition-all rounded-lg hover:bg-gray-100"
                        style={{ color: '#8B7E6A', background: 'rgba(139, 126, 106, 0.1)' }}
                        data-testid="skip-debt"
                      >
                        I'm not focusing on debt this year
                      </button>
                    </div>
                  </div>
                )}

                {/* Step: Savings - habit-based, no timeline */}
                {currentStepId === 'savings' && (
                  <div className="flex-1 flex flex-col items-center justify-center animate-fade-up-soft">
                    <div className="w-20 h-20 blob-1 flex items-center justify-center mb-6" style={{ background: 'rgba(46, 134, 171, 0.15)' }}>
                      <PiggyBank className="w-10 h-10" style={{ color: '#2E86AB' }} />
                    </div>
                    <h3 className="font-heading text-2xl font-medium mb-2 text-center" style={{ color: '#2A1E5C' }}>
                      How much do you want to save this year?
                    </h3>
                    <p className="font-body text-sm mb-8 text-center max-w-md" style={{ color: '#8B7E6A' }}>
                      Unlike debt, savings isn't a race. We'll spread this across the year so it becomes part of your weekly rhythm.
                    </p>
                    <div className="w-full max-w-sm space-y-6">
                      <div className="relative">
                        <span className="absolute left-4 top-1/2 -translate-y-1/2 font-heading text-2xl" style={{ color: '#C4B8A8' }}>$</span>
                        <input
                          type="number"
                          value={formData.savingsGoal}
                          onChange={(e) => {
                            setFormData({ ...formData, savingsGoal: e.target.value, savingsMonths: '12' });
                          }}
                          placeholder="3,000"
                          step="500"
                          min="0"
                          className="w-full font-heading text-3xl text-center pl-10 pr-4 py-4 border-b-3 bg-transparent focus:outline-none transition"
                          style={{ color: '#2A1E5C', borderBottomWidth: '3px', borderColor: '#E4DCD2' }}
                          data-testid="savings-input"
                          autoFocus
                        />
                      </div>
                      {formData.savingsGoal && parseFloat(formData.savingsGoal) > 0 && (
                        <div className="text-center p-4 rounded-xl" style={{ background: 'rgba(46, 134, 171, 0.1)' }}>
                          <p className="font-body text-base mb-1" style={{ color: '#2E86AB' }}>
                            That's about <strong>{formatCurrency(parseFloat(formData.savingsGoal) / 48)}/week</strong> toward your future self
                          </p>
                          <p className="font-body text-sm" style={{ color: '#8B7E6A' }}>
                            or about {formatCurrency(parseFloat(formData.savingsGoal) / 12)}/month
                          </p>
                          {formData.hasTargetInMind && formData.monthlyTarget && (() => {
                            const monthlySavings = parseFloat(formData.savingsGoal) / 12;
                            const targetNum = parseFloat(formData.monthlyTarget) || 0;
                            return (
                              <p className="font-body text-sm mt-3 pt-3 border-t" style={{ color: '#2A1E5C', borderColor: 'rgba(46, 134, 171, 0.2)' }} data-testid="savings-target-heads-up">
                                That's about <strong>{formatCurrency(monthlySavings)}/month</strong> carved out of your <strong>{formatCurrency(targetNum)}/month</strong> target. We'll flag on the next screen if it doesn't leave enough for your expenses.
                              </p>
                            );
                          })()}
                        </div>
                      )}
                      
                      {/* Skip option */}
                      <button
                        onClick={() => {
                          setFormData({ ...formData, savingsGoal: '', savingsMonths: '' });
                          setCurrentStep(prev => prev + 1);
                        }}
                        className="w-full py-3 font-body text-sm transition-all rounded-lg hover:bg-gray-100"
                        style={{ color: '#8B7E6A', background: 'rgba(139, 126, 106, 0.1)' }}
                        data-testid="skip-savings"
                      >
                        I'm not focusing on savings this year
                      </button>
                    </div>
                  </div>
                )}

                {/* Step: Weeks Off */}
                {currentStepId === 'weeks' && (
                  <div className="flex-1 flex flex-col items-center justify-center animate-fade-up-soft">
                    <div className="w-20 h-20 blob-2 flex items-center justify-center mb-6" style={{ background: 'rgba(17, 75, 95, 0.15)' }}>
                      <Calendar className="w-10 h-10" style={{ color: '#114B5F' }} />
                    </div>
                    <h3 className="font-heading text-2xl font-medium mb-2 text-center" style={{ color: '#2A1E5C' }}>
                      How many weeks do you want to take off this year?
                    </h3>
                    <p className="font-body text-sm mb-8 text-center max-w-md" style={{ color: '#8B7E6A' }}>
                      This determines how many working weeks you'll have and builds in funded time off.
                    </p>
                    <div className="w-full max-w-md space-y-3">
                      {WEEKS_OFF_OPTIONS.map((option) => (
                        <button
                          key={option.value}
                          onClick={() => setFormData({ ...formData, weeksOff: option.value })}
                          className={`w-full p-4 rounded-xl text-left transition-all flex items-center justify-between ${
                            formData.weeksOff === option.value
                              ? 'bg-white shadow-lg ring-2 ring-yellow-400'
                              : 'bg-gray-50 hover:bg-white hover:shadow-md'
                          }`}
                          data-testid={`weeks-off-${option.value}`}
                        >
                          <div>
                            <span className="font-heading font-medium" style={{ color: '#2A1E5C' }}>{option.label}</span>
                            <span className="font-body text-sm ml-2" style={{ color: '#8B7E6A' }}>({option.description})</span>
                          </div>
                          {formData.weeksOff === option.value && (
                            <CheckCircle2 className="w-5 h-5 text-yellow-500" />
                          )}
                        </button>
                      ))}
                      
                      {formData.weeksOff === 'custom' && (
                        <div className="mt-4">
                          <input
                            type="number"
                            value={formData.customWeeksOff}
                            onChange={(e) => setFormData({ ...formData, customWeeksOff: e.target.value })}
                            placeholder="Enter weeks off (0-51)"
                            min="0"
                            max="51"
                            className="w-full font-body text-lg text-center p-3 rounded-xl bg-white border-2 border-yellow-400 focus:outline-none"
                            style={{ color: '#2A1E5C' }}
                            data-testid="custom-weeks-off-input"
                            autoFocus
                          />
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Step: Days Per Week */}
                {currentStepId === 'days' && (
                  <div className="flex-1 flex flex-col items-center justify-center animate-fade-up-soft">
                    <div className="w-20 h-20 blob-1 flex items-center justify-center mb-6" style={{ background: 'rgba(245, 203, 92, 0.15)' }}>
                      <Clock className="w-10 h-10" style={{ color: '#F5CB5C' }} />
                    </div>
                    <h3 className="font-heading text-2xl font-medium mb-2 text-center" style={{ color: '#2A1E5C' }}>
                      How many days a week do you want to work?
                    </h3>
                    <p className="font-body text-sm mb-8 text-center max-w-md" style={{ color: '#8B7E6A' }}>
                      Choose the number of days you realistically want to dedicate to this work each week.
                    </p>
                    <div className="flex justify-center gap-3 flex-wrap">
                      {[1, 2, 3, 4, 5, 6, 7].map((days) => (
                        <button
                          key={days}
                          onClick={() => setFormData({ ...formData, daysPerWeek: days })}
                          className={`w-14 h-14 rounded-2xl font-heading text-xl font-medium transition-all ${
                            formData.daysPerWeek === days
                              ? 'bg-yellow-400 text-gray-900 shadow-lg scale-110'
                              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                          }`}
                          data-testid={`days-${days}`}
                        >
                          {days}
                        </button>
                      ))}
                    </div>
                    <p className="font-body text-sm mt-4 text-center" style={{ color: '#8B7E6A' }}>
                      {formData.daysPerWeek} day{formData.daysPerWeek !== 1 ? 's' : ''} per week
                    </p>
                  </div>
                )}

                {/* Step: Taxes */}
                {currentStepId === 'taxes' && (
                  <div className="flex-1 flex flex-col items-center justify-center animate-fade-up-soft">
                    <div className="w-20 h-20 blob-2 flex items-center justify-center mb-6" style={{ background: 'rgba(222, 60, 75, 0.15)' }}>
                      <DollarSign className="w-10 h-10" style={{ color: '#DE3C4B' }} />
                    </div>
                    <h3 className="font-heading text-2xl font-medium mb-2 text-center" style={{ color: '#2A1E5C' }}>
                      Let's account for your taxes
                    </h3>
                    <p className="font-body text-sm mb-6 text-center max-w-lg" style={{ color: '#8B7E6A' }}>
                      As a 1099 worker, you're responsible for self-employment and federal income taxes. 
                      We'll calculate what you need to <strong>gross</strong> to <strong>net</strong> your goal.
                    </p>
                    
                    {/* Self-Employment Tax - Fixed */}
                    <div className="w-full max-w-md mb-6 p-4 rounded-xl" style={{ background: 'rgba(222, 60, 75, 0.08)' }}>
                      <div className="flex justify-between items-center">
                        <div>
                          <p className="font-heading font-medium" style={{ color: '#2A1E5C' }}>Self-Employment Tax</p>
                          <p className="font-body text-xs" style={{ color: '#8B7E6A' }}>Social Security + Medicare (required for all 1099 income)</p>
                        </div>
                        <span className="font-heading text-xl font-medium" style={{ color: '#DE3C4B' }}>14.1%</span>
                      </div>
                      <p className="font-body text-xs mt-2 italic" style={{ color: '#A89F8F' }}>
                        Why 14.1%? Self-employment tax is 15.3%, but it's only applied to 92.35% of net income, making the effective rate about 14.1% for planning.
                      </p>
                    </div>

                    {/* Federal Income Tax - Slider */}
                    <div className="w-full max-w-md mb-4">
                      <div className="flex justify-between items-center mb-3">
                        <p className="font-heading font-medium" style={{ color: '#2A1E5C' }}>Federal Income Tax</p>
                        <span className="font-heading text-xl font-medium" style={{ color: '#2E86AB' }}>{formData.federalTaxRate}%</span>
                      </div>
                      <input
                        type="range"
                        min="0"
                        max="15"
                        value={formData.federalTaxRate}
                        onChange={(e) => setFormData({ ...formData, federalTaxRate: parseInt(e.target.value) })}
                        className="w-full h-3 rounded-full appearance-none cursor-pointer"
                        style={{ 
                          background: `linear-gradient(to right, #2E86AB 0%, #2E86AB ${(formData.federalTaxRate / 15) * 100}%, #E4DCD2 ${(formData.federalTaxRate / 15) * 100}%, #E4DCD2 100%)` 
                        }}
                        data-testid="federal-tax-slider"
                      />
                      <div className="flex justify-between mt-2">
                        <span className="font-body text-xs" style={{ color: '#8B7E6A' }}>0%</span>
                        <span className="font-body text-xs" style={{ color: '#8B7E6A' }}>15%</span>
                      </div>
                      <p className="font-body text-xs text-center mt-3" style={{ color: '#A89F8F' }}>
                        Most people set aside 10-15%. Choose 0% if you qualify for exemptions (like foreign earned income exclusion).
                      </p>
                    </div>

                    {/* Total Tax Rate */}
                    <div className="w-full max-w-md p-4 rounded-xl" style={{ background: 'rgba(42, 30, 92, 0.05)' }}>
                      <div className="flex justify-between items-center">
                        <p className="font-heading font-medium" style={{ color: '#2A1E5C' }}>Total Tax Set-Aside</p>
                        <span className="font-heading text-2xl font-medium" style={{ color: '#2A1E5C' }}>
                          {(14.1 + formData.federalTaxRate).toFixed(1)}%
                        </span>
                      </div>
                    </div>
                  </div>
                )}

                {/* Step: Buffer (Expenses path only) */}
                {currentStepId === 'buffer' && (
                  <div className="flex flex-col items-center">
                    <h3 className="font-heading text-2xl sm:text-3xl font-medium mb-3 text-center" style={{ color: '#2A1E5C' }}>
                      Add a buffer for slow weeks?
                    </h3>
                    <p className="font-body text-base mb-8 max-w-lg text-center" style={{ color: '#5D5A6B' }}>
                      Based on your expenses and goals, we've calculated the <strong>minimum</strong> you need to earn. 
                      Adding a buffer gives you breathing room when work is slow.
                    </p>

                    {/* Buffer Slider */}
                    <div className="w-full max-w-md mb-6">
                      <div className="flex justify-between items-center mb-3">
                        <p className="font-heading font-medium" style={{ color: '#2A1E5C' }}>Buffer for Slow Weeks</p>
                        <span className="font-heading text-xl font-medium" style={{ color: '#2A1E5C' }}>{formData.slowWeekBuffer}%</span>
                      </div>
                      <input
                        type="range"
                        min="0"
                        max="30"
                        step="5"
                        value={formData.slowWeekBuffer}
                        onChange={(e) => setFormData({ ...formData, slowWeekBuffer: parseInt(e.target.value) })}
                        className="w-full h-3 rounded-full appearance-none cursor-pointer"
                        style={{ 
                          background: `linear-gradient(to right, #2A1E5C 0%, #2A1E5C ${(formData.slowWeekBuffer / 30) * 100}%, #E4DCD2 ${(formData.slowWeekBuffer / 30) * 100}%, #E4DCD2 100%)` 
                        }}
                        data-testid="buffer-slider"
                      />
                      <div className="flex justify-between mt-2">
                        <span className="font-body text-xs" style={{ color: '#8B7E6A' }}>0% (minimum)</span>
                        <span className="font-body text-xs" style={{ color: '#8B7E6A' }}>30% (comfortable)</span>
                      </div>
                    </div>

                    {/* Buffer explanation */}
                    <div className="w-full max-w-md p-4 rounded-xl" style={{ background: 'rgba(42, 30, 92, 0.05)' }}>
                      <p className="font-body text-sm text-center" style={{ color: '#5D5A6B' }}>
                        {formData.slowWeekBuffer === 0 ? (
                          <>This is your <strong>exact minimum</strong>. Every week needs to hit your target. No room for slow weeks.</>
                        ) : formData.slowWeekBuffer <= 10 ? (
                          <>A <strong>{formData.slowWeekBuffer}% buffer</strong> gives you a small cushion. One or two slow weeks won't derail you.</>
                        ) : formData.slowWeekBuffer <= 20 ? (
                          <>A <strong>{formData.slowWeekBuffer}% buffer</strong> is solid. You'll have breathing room for a slow month without stress.</>
                        ) : (
                          <>A <strong>{formData.slowWeekBuffer}% buffer</strong> is comfortable. You'll bank a bonus even in slower seasons.</>
                        )}
                      </p>
                    </div>
                  </div>
                )}

                {/* Navigation */}
                <div className="flex justify-between items-center mt-10 pt-8 border-t" style={{ borderColor: 'rgba(42, 30, 92, 0.08)' }}>
                  <button
                    onClick={handleBack}
                    disabled={currentStep === 0}
                    className={`btn-soft btn-secondary-soft flex items-center gap-2 ${currentStep === 0 ? 'opacity-30 cursor-not-allowed' : ''}`}
                    data-testid="back-btn"
                  >
                    <ArrowLeft className="w-4 h-4" />
                    Back
                  </button>
                  <button
                    onClick={handleNext}
                    disabled={!canProceed()}
                    className={`btn-soft btn-primary-soft flex items-center gap-2 ${!canProceed() ? 'opacity-50 cursor-not-allowed' : ''}`}
                    data-testid="next-btn"
                  >
                    {currentStep === steps.length - 1 ? 'See My Number' : 'Next'}
                    <ArrowRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          ) : (
            /* Results */
            <div id="results-section" className="animate-fade-up-soft" data-testid="results">
              <div className="text-center mb-10">
                <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full mb-4" style={{ background: 'rgba(42, 30, 92, 0.1)' }}>
                  <Sparkles className="w-4 h-4" style={{ color: '#2A1E5C' }} />
                  <span className="font-body text-sm" style={{ color: '#2A1E5C' }}>Your weekly income target (GROSS)</span>
                </div>
                <h3 className="font-heading text-5xl sm:text-6xl font-medium mb-2" style={{ color: '#2A1E5C' }}>
                  {formatCurrency(calculateRelayTarget())}
                  <span className="text-2xl font-normal" style={{ color: '#5D5A6B' }}>/week</span>
                </h3>
                <p className="font-heading text-2xl mb-4" style={{ color: '#F5CB5C' }}>
                  {formatCurrency(calculateDailyTarget())}
                  <span className="text-lg font-normal" style={{ color: '#8B7E6A' }}>/day average</span>
                </p>
                <p className="font-body text-base max-w-xl mx-auto mb-6" style={{ color: '#5D5A6B' }}>
                  {formData.hasTargetInMind && formData.monthlyTarget ? (
                    <>
                      This is what you need to <strong style={{ color: '#2A1E5C' }}>gross</strong> each week in order to
                      <strong style={{ color: '#2A1E5C' }}> take home {formatCurrency(parseFloat(formData.monthlyTarget))}/month</strong> after taxes.
                    </>
                  ) : (
                    <>
                      This is what you need to <strong style={{ color: '#2A1E5C' }}>gross</strong> each week. After taxes,
                      you'll <strong style={{ color: '#2A1E5C' }}>net</strong> {formatCurrency(getTaxBreakdown().netWeekly)}/week to cover your goals.
                    </>
                  )}
                </p>

                {/* SMART Reality Check Section - Immediately after main result */}
                {(() => {
                  const feasibility = getFeasibilityCheck();
                  const statusConfig = {
                    'on-track': { 
                      icon: '✓', 
                      label: 'On Track!', 
                      bgColor: 'rgba(76, 175, 80, 0.15)', 
                      borderColor: '#4CAF50',
                      textColor: '#4CAF50'
                    },
                    'solid': { 
                      icon: '✓', 
                      label: 'Solid!', 
                      bgColor: 'rgba(76, 175, 80, 0.15)', 
                      borderColor: '#4CAF50',
                      textColor: '#4CAF50'
                    },
                    'nice': { 
                      icon: '✓', 
                      label: 'Nice!', 
                      bgColor: 'rgba(76, 175, 80, 0.15)', 
                      borderColor: '#4CAF50',
                      textColor: '#4CAF50'
                    },
                    'great': { 
                      icon: '✓', 
                      label: 'Great!', 
                      bgColor: 'rgba(76, 175, 80, 0.15)', 
                      borderColor: '#4CAF50',
                      textColor: '#4CAF50'
                    },
                    'perfect': { 
                      icon: '✓', 
                      label: 'Perfect!', 
                      bgColor: 'rgba(76, 175, 80, 0.15)', 
                      borderColor: '#4CAF50',
                      textColor: '#4CAF50'
                    },
                    'tight': { 
                      icon: '→', 
                      label: 'Tight!', 
                      bgColor: 'rgba(255, 193, 7, 0.15)', 
                      borderColor: '#FFC107',
                      textColor: '#FFC107'
                    },
                    'not-realistic': { 
                      icon: '!', 
                      label: 'Uh-oh!', 
                      bgColor: 'rgba(222, 60, 75, 0.15)', 
                      borderColor: '#DE3C4B',
                      textColor: '#DE3C4B'
                    },
                    'unknown': { 
                      icon: '✓', 
                      label: 'Sanity Check', 
                      bgColor: 'rgba(46, 134, 171, 0.15)', 
                      borderColor: '#2E86AB',
                      textColor: '#2E86AB'
                    }
                  };
                  const config = statusConfig[feasibility.status] || statusConfig['unknown'];
                  
                  return (
                    <div className="max-w-lg mx-auto mb-8">
                      <div 
                        className="p-5 rounded-2xl border-2"
                        style={{ 
                          background: config.bgColor,
                          borderColor: config.borderColor
                        }}
                      >
                        {/* Status Header */}
                        <div className="flex items-center justify-center gap-3 mb-3">
                          <span 
                            className="w-8 h-8 rounded-full flex items-center justify-center font-bold text-lg"
                            style={{ background: config.borderColor, color: '#2A1E5C' }}
                          >
                            {config.icon}
                          </span>
                          <span 
                            className="font-heading text-xl font-medium"
                            style={{ color: config.textColor }}
                          >
                            {config.label}
                          </span>
                        </div>
                        
                        {/* Message */}
                        <p className="font-body text-sm text-center mb-4" style={{ color: '#2A1E5C' }}>
                          {feasibility.message}
                        </p>
                        
                        {/* Suggestion (if applicable) */}
                        {feasibility.suggestion && (
                          <p className="font-body text-xs text-center italic" style={{ color: '#5D5A6B' }}>
                            {feasibility.suggestion}
                          </p>
                        )}
                      </div>
                    </div>
                  );
                })()}

                {/* Tax Breakdown */}
                <div className="max-w-lg mx-auto mb-6">
                  <div className="p-5 rounded-2xl" style={{ background: 'rgba(42, 30, 92, 0.05)' }}>
                    <p className="font-body text-xs uppercase tracking-widest mb-4 text-center" style={{ color: '#5D5A6B' }}>Tax Breakdown</p>
                    <div className="space-y-3">
                      <div className="flex justify-between items-center">
                        <span className="font-body text-sm" style={{ color: '#5D5A6B' }}>Self-employment tax (14.1%)</span>
                        <span className="font-heading font-medium" style={{ color: '#DE3C4B' }}>−{formatCurrency(getTaxBreakdown().selfEmploymentTax)}</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="font-body text-sm" style={{ color: '#5D5A6B' }}>Federal income tax ({formData.federalTaxRate}%)</span>
                        <span className="font-heading font-medium" style={{ color: '#DE3C4B' }}>−{formatCurrency(getTaxBreakdown().federalTax)}</span>
                      </div>
                      <div className="border-t pt-4 mt-4" style={{ borderColor: 'rgba(42, 30, 92, 0.1)' }}>
                        <div className="text-center">
                          <p className="font-body text-sm mb-2" style={{ color: '#5D5A6B' }}>Your weekly take-home</p>
                          <p className="font-heading text-4xl font-medium mb-1" style={{ color: '#4CAF50' }}>{formatCurrency(getTaxBreakdown().netWeekly)}</p>
                          <p className="font-body text-xs" style={{ color: '#8B7E6A' }}>What you keep after taxes to cover your goals</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Format & Color picker */}
              <div className="flex flex-col sm:flex-row justify-center items-center gap-4 sm:gap-6 mb-8">
                {/* Format selector */}
                <div className="flex items-center gap-2 px-4 py-2 rounded-full" style={{ background: 'rgba(42, 30, 92, 0.08)' }}>
                  <span className="font-body text-xs" style={{ color: '#5D5A6B' }}>Format:</span>
                  {[
                    { id: 'landscape', label: 'Landscape' },
                    { id: 'portrait', label: 'Portrait' },
                    { id: 'story', label: 'Story' }
                  ].map((format) => (
                    <button
                      key={format.id}
                      onClick={() => setPlacardFormat(format.id)}
                      className={`px-3 py-1 rounded-full font-body text-xs transition-all ${
                        placardFormat === format.id 
                          ? 'text-white' 
                          : 'hover:opacity-80'
                      }`}
                      style={{ 
                        background: placardFormat === format.id ? '#2A1E5C' : 'transparent',
                        color: placardFormat === format.id ? '#FFFFFF' : '#5D5A6B'
                      }}
                      data-testid={`format-${format.id}`}
                    >
                      {format.label}
                    </button>
                  ))}
                </div>
                
                {/* Color picker */}
                <div className="flex items-center gap-3">
                  <Palette className="w-4 h-4" style={{ color: '#5D5A6B' }} />
                  {accentColors.map((color) => (
                    <button
                      key={color.hex}
                      onClick={() => setPlacardColor(color.hex)}
                      className={`w-8 h-8 rounded-full transition-all ${placardColor === color.hex ? 'ring-2 ring-gray-800 scale-110' : 'hover:scale-105'}`}
                      style={{ background: color.hex }}
                      title={color.name}
                      data-testid={`color-${color.name.toLowerCase()}`}
                    />
                  ))}
                </div>
              </div>

              {/* Goal Card - Centered */}
              <div className="relative flex justify-center mb-8">
                {/* Floating "Already have Payday Relay?" bubble - positioned to the side */}
                <div 
                  className="hidden lg:block absolute -right-4 top-1/2 -translate-y-1/2 translate-x-full z-10"
                  style={{ maxWidth: '200px' }}
                >
                  <div 
                    className="p-4 rounded-2xl relative"
                    style={{ 
                      background: `${placardColor}20`, 
                      border: `2px solid ${placardColor}`,
                      boxShadow: `0 0 20px ${placardColor}25`,
                      borderRadius: '20px 20px 20px 4px'
                    }}
                  >
                    {/* Speech bubble tail */}
                    <div 
                      className="absolute -left-3 top-1/2 -translate-y-1/2 w-0 h-0"
                      style={{
                        borderTop: '10px solid transparent',
                        borderBottom: '10px solid transparent',
                        borderRight: `12px solid ${placardColor}`
                      }}
                    />
                    <div 
                      className="absolute -left-2 top-1/2 -translate-y-1/2 w-0 h-0"
                      style={{
                        borderTop: '8px solid transparent',
                        borderBottom: '8px solid transparent',
                        borderRight: `10px solid ${placardColor}20`
                      }}
                    />
                    
                    <div className="text-center mb-3">
                      <Zap className="w-5 h-5 mx-auto mb-1" style={{ color: placardColor }} />
                      <span className="font-heading text-sm font-medium" style={{ color: placardColor }}>
                        Already have Payday Relay?
                      </span>
                    </div>
                    <p className="font-body text-xs mb-3 text-center" style={{ color: '#5D5A6B' }}>
                      Copy & paste your goal into your Legend!
                    </p>
                    <button 
                      onClick={copyGoalToClipboard}
                      className="w-full py-2 px-3 rounded-lg font-heading font-medium text-xs flex items-center justify-center gap-1.5 transition-all hover:scale-105"
                      style={{ 
                        background: goalCopied ? '#4CAF50' : placardColor, 
                        color: goalCopied ? '#FFFFFF' : (placardColor === '#F5CB5C' ? '#2A1E5C' : '#FFFFFF')
                      }}
                      data-testid="copy-goal-btn"
                    >
                      {goalCopied ? (
                        <>
                          <Check className="w-3.5 h-3.5" />
                          Copied!
                        </>
                      ) : (
                        <>
                          <Copy className="w-3.5 h-3.5" />
                          Copy Goal
                        </>
                      )}
                    </button>
                  </div>
                </div>

                {/* Main Placard */}
                <div 
                  ref={placardRef}
                  className={`placard-artsy transition-all duration-300 ${
                    placardFormat === 'landscape' ? 'w-full max-w-2xl' :
                    placardFormat === 'portrait' ? 'w-full max-w-sm' :
                    'w-full max-w-xs'
                  }`}
                >
                  <div className="relative overflow-hidden" style={{ background: `linear-gradient(135deg, ${placardColor} 0%, ${placardColor}dd 100%)`, padding: placardFormat === 'landscape' ? '28px 36px' : '20px 24px' }}>
                    <div className="absolute top-0 right-0 w-32 h-32 blob-1" style={{ background: 'rgba(255, 255, 255, 0.1)', transform: 'translate(30%, -30%)' }} />
                    <div className={`relative flex items-center ${placardFormat === 'landscape' ? 'justify-between' : 'justify-center'}`}>
                      <div className="flex items-center gap-3">
                        <Zap className={placardFormat === 'landscape' ? 'w-6 h-6' : 'w-5 h-5'} style={{ color: placardColor === '#F5CB5C' ? '#2A1E5C' : '#F5CB5C' }} />
                        <span className={`font-heading font-medium ${placardFormat === 'landscape' ? 'text-xl' : 'text-lg'}`} style={{ color: placardColor === '#F5CB5C' ? '#2A1E5C' : '#FFFFFF' }}>My Weekly Income Target</span>
                      </div>
                      {placardFormat === 'landscape' && (
                        <span className="font-body text-sm" style={{ color: placardColor === '#F5CB5C' ? '#2A1E5C' : '#F5CB5C' }}>
                          {selectedPaths.length} goal{selectedPaths.length > 1 ? 's' : ''}, one number
                        </span>
                      )}
                    </div>
                  </div>
                  <div style={{ padding: placardFormat === 'landscape' ? '32px 40px' : placardFormat === 'portrait' ? '24px' : '20px' }}>
                    <p className={`font-body leading-relaxed mb-4 ${placardFormat === 'landscape' ? 'text-lg' : 'text-base'}`} style={{ color: '#2A1E5C' }}>
                      {getGoalStatementJSX()}
                    </p>
                    <div className={`rounded-2xl mb-4 ${placardFormat === 'landscape' ? 'p-6' : 'p-4'}`} style={{ background: 'rgba(42, 30, 92, 0.04)' }}>
                      <div className="text-center">
                        <p className="font-body text-xs uppercase tracking-widest mb-1" style={{ color: '#8B7E6A' }}>Gross Weekly Target</p>
                        <p className={`font-heading font-medium ${placardFormat === 'landscape' ? 'text-5xl' : placardFormat === 'portrait' ? 'text-4xl' : 'text-3xl'}`} style={{ color: placardColor }}>
                          {formatCurrency(calculateRelayTarget())}
                        </p>
                        <p className={`font-heading font-medium mt-2 ${placardFormat === 'landscape' ? 'text-2xl' : 'text-xl'}`} style={{ color: '#F5CB5C' }}>
                          {formatCurrency(calculateDailyTarget())}/day
                        </p>
                        <p className={`font-body font-medium mt-3 ${placardFormat === 'landscape' ? 'text-sm' : 'text-sm'}`} style={{ color: '#28A745' }}>
                          Net after taxes: {formatCurrency(getTaxBreakdown().netWeekly)}/week
                        </p>
                        <p className="font-body text-xs mt-1" style={{ color: '#A89F8F' }}>
                          {formData.daysPerWeek} days/week • {getWorkingWeeks()} weeks • {getWeeksOff()} weeks off
                        </p>
                        {getWeeksOff() > 0 && (
                          <div className="mt-2">
                            <p className="font-body text-xs font-medium" style={{ color: '#2E86AB' }}>
                              Time-off buffer: {formatCurrency(getTimeOffBuffer())}/week for {getWeeksOff()} weeks PTO
                            </p>
                            <p className="font-body text-xs mt-1 italic" style={{ color: '#8B8178' }}>
                              Your PTO bank is based on your <strong style={{ fontStyle: 'normal' }}>after-tax weekly target</strong>, so it replaces your take-home rhythm during time off without taxing the same money twice.
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                    {/* Payday Relay Logo */}
                    <div className="flex justify-center pt-4 border-t" style={{ borderColor: 'rgba(42, 30, 92, 0.08)' }}>
                      <img loading="lazy" 
                        src="/images/paydayrelaylogo.png"
                        alt="Payday Relay"
                        className={placardFormat === 'landscape' ? 'h-16' : 'h-14'}
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Mobile-only Copy Section */}
              <div className="lg:hidden max-w-md mx-auto mb-6 p-4 rounded-xl" style={{ background: `${placardColor}15`, border: `2px solid ${placardColor}` }}>
                <div className="flex items-center gap-2 mb-2">
                  <Zap className="w-4 h-4" style={{ color: placardColor }} />
                  <span className="font-heading text-sm font-medium" style={{ color: placardColor }}>
                    Already have Payday Relay?
                  </span>
                </div>
                <p className="font-body text-xs mb-3" style={{ color: '#5D5A6B' }}>
                  Copy your goal and paste it into your Legend in the sheet.
                </p>
                <button 
                  onClick={copyGoalToClipboard}
                  className="w-full py-2 px-4 rounded-lg font-heading font-medium text-sm flex items-center justify-center gap-2 transition-all"
                  style={{ 
                    background: goalCopied ? '#4CAF50' : placardColor, 
                    color: goalCopied ? '#FFFFFF' : (placardColor === '#F5CB5C' ? '#2A1E5C' : '#FFFFFF')
                  }}
                >
                  {goalCopied ? (
                    <>
                      <Check className="w-4 h-4" />
                      Copied!
                    </>
                  ) : (
                    <>
                      <Copy className="w-4 h-4" />
                      Copy My Goal
                    </>
                  )}
                </button>
              </div>

              {/* Start Over Button - Hidden after email sent */}
              {!emailSent && (
                <div className="flex flex-col items-center mt-6 mb-8">
                  <button 
                    onClick={resetCalculator} 
                    className="flex items-center gap-2 px-5 py-2 rounded-lg font-body text-sm transition-all hover:bg-gray-100"
                    style={{ color: '#5D5A6B', border: '1px solid rgba(42, 30, 92, 0.2)' }}
                    data-testid="reset-btn"
                  >
                    <ArrowLeft className="w-4 h-4" />
                    Start Over
                  </button>
                  <p className="font-body text-xs mt-3" style={{ color: '#8B7E6A' }}>
                    Use it as many times as you want.
                  </p>
                </div>
              )}

              {/* Share Section */}
              <div className="flex flex-col items-center">
                <div className="w-full max-w-xl">
                  <div className="text-center mb-4">
                    <p className="font-heading text-lg font-medium mb-1" style={{ color: '#2A1E5C' }}>Share your goal</p>
                    <p className="font-body text-sm" style={{ color: '#5D5A6B' }}>
                      Post to social media with <span style={{ color: '#2A1E5C', fontWeight: '600' }}>#PaydayRelay</span>
                    </p>
                  </div>
                  
                  <div className="flex flex-col sm:flex-row justify-center gap-3 mb-4">
                    <button 
                      onClick={async () => {
                        await downloadPlacardForShare();
                        toast.success('Image downloaded!');
                      }} 
                      className="flex items-center justify-center gap-2 px-6 py-3 rounded-xl font-heading font-medium transition-all hover:scale-105"
                      style={{ background: '#F5CB5C', color: '#2A1E5C' }}
                    >
                      <Download className="w-5 h-5" />
                      Download Image
                    </button>
                    <button 
                      onClick={async () => {
                        await navigator.clipboard.writeText(getShareText());
                        setCopied(true);
                        toast.success('Caption copied!');
                        setTimeout(() => setCopied(false), 2000);
                      }} 
                      className="flex items-center justify-center gap-2 px-6 py-3 rounded-xl font-heading font-medium transition-all hover:scale-105"
                      style={{ background: 'rgba(42, 30, 92, 0.08)', color: '#2A1E5C', border: '1px solid rgba(42, 30, 92, 0.2)' }}
                    >
                      {copied ? <Check className="w-5 h-5" /> : <Copy className="w-5 h-5" />}
                      {copied ? 'Copied!' : 'Copy Caption'}
                    </button>
                  </div>

                  <button 
                    onClick={() => setShowSharePreview(!showSharePreview)} 
                    className="font-body text-xs flex items-center gap-1 mx-auto" 
                    style={{ color: '#5D5A6B' }}
                  >
                    <Eye className="w-3 h-3" />
                    {showSharePreview ? 'Hide' : 'Preview'} caption
                  </button>
                  
                  {showSharePreview && (
                    <div className="mt-4 p-4 rounded-xl text-sm whitespace-pre-line" style={{ background: 'rgba(42, 30, 92, 0.05)', border: '1px solid rgba(42, 30, 92, 0.1)' }}>
                      <p className="font-body" style={{ color: '#2A1E5C' }}>{getShareText()}</p>
                    </div>
                  )}
                </div>
              </div>

              {/* ========== FINANCIAL TRAJECTORY SECTION (always shows) ========== */}
              {(() => {
                const story = getTrajectoryStory();
                const weeklyTarget = calculateRelayTarget();
                const dailyTarget = calculateDailyTarget();
                const daysPerWeek = parseInt(formData.daysPerWeek) || 5;
                const workWeeks = getWorkingWeeks();
                
                // STORY 1: Expenses Only - "Your Steady Rhythm"
                if (story === 'expenses-only') {
                  const weeksOff = 52 - workWeeks;
                  const timeOffBufferPerWeek = getTimeOffBuffer();
                  const totalTimeOffFund = timeOffBufferPerWeek * workWeeks;
                  return (
                    <div className="max-w-4xl mx-auto mt-16" data-testid="timeline-section">
                      {/* Dark background container for 12-month trajectory */}
                      <div className="p-8 sm:p-10 rounded-2xl" style={{ background: '#1E1640' }}>
                        <div className="text-center mb-8">
                          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full mb-4" style={{ background: 'rgba(245, 203, 92, 0.15)' }}>
                            <Calendar className="w-4 h-4" style={{ color: '#F5CB5C' }} />
                            <span className="font-body text-sm" style={{ color: '#F5CB5C' }}>Your 12-Month Rhythm</span>
                          </div>
                          <h3 className="font-heading text-2xl sm:text-3xl font-medium mb-2" style={{ color: '#FFFFFF' }}>
                            Stability through consistency.
                          </h3>
                          <p className="font-body text-sm" style={{ color: 'rgba(255,255,255,0.6)' }}>
                            Hit your weekly target, week after week, and your expenses are covered all year.
                          </p>
                        </div>
                        <div className="relative h-3 rounded-full overflow-hidden mb-8" style={{ background: 'rgba(255,255,255,0.1)' }}>
                          <div className="absolute h-full w-full" style={{ background: '#F5CB5C' }} />
                        </div>
                        <div className="p-8 rounded-xl text-center" style={{ background: 'rgba(255,255,255,0.05)', border: '2px solid #F5CB5C' }}>
                          <p className="font-body text-xs uppercase tracking-wider mb-4" style={{ color: '#F5CB5C' }}>Your Weekly Target</p>
                          <p className="font-heading text-5xl font-medium mb-2" style={{ color: '#FFFFFF' }}>{formatCurrency(weeklyTarget)}</p>
                          <p className="font-heading text-xl mb-4" style={{ color: '#F5CB5C' }}>{formatCurrency(dailyTarget)}/day × {daysPerWeek} days</p>
                          
                          {weeksOff > 0 && (
                            <>
                              <p className="font-body text-sm mb-6" style={{ color: 'rgba(255,255,255,0.7)' }}>
                                Includes each week: <span style={{ color: '#FFFFFF' }}>{formatCurrency(timeOffBufferPerWeek)} towards time off</span>
                              </p>
                              
                              <div className="mb-6 p-4 rounded-lg" style={{ background: 'rgba(255,255,255,0.05)' }}>
                                <p className="font-body text-sm mb-3 font-medium" style={{ color: '#FFFFFF' }}>Your {weeksOff}-week time-off fund</p>
                                <div className="flex justify-center gap-8 text-center">
                                  <div>
                                    <p className="font-body text-xs mb-1" style={{ color: 'rgba(255,255,255,0.5)' }}>Total saved</p>
                                    <p className="font-heading text-xl font-medium" style={{ color: '#FFFFFF' }}>{formatCurrency(totalTimeOffFund)}</p>
                                  </div>
                                  <div>
                                    <p className="font-body text-xs mb-1" style={{ color: 'rgba(255,255,255,0.5)' }}>For each week you take off</p>
                                    <p className="font-heading text-xl font-medium" style={{ color: '#FFFFFF' }}>{formatCurrency(totalTimeOffFund / weeksOff)}</p>
                                  </div>
                                </div>
                              </div>
                            </>
                          )}
                          
                          <div className="pt-6 border-t" style={{ borderColor: 'rgba(255,255,255,0.1)' }}>
                            <p className="font-body text-base" style={{ color: 'rgba(255,255,255,0.9)' }}>{workWeeks} weeks of hitting this target = <span style={{ color: '#F5CB5C' }}>a year of stability</span></p>
                          </div>
                        </div>
                        <p className="font-body text-sm text-center mt-4" style={{ color: 'rgba(255,255,255,0.5)' }}>Beat your target? That extra goes to your bonus bank, ready for debt payoff, savings, or breathing room.</p>
                      </div>
                    </div>
                  );
                }

                // TARGET MODE - User has a specific monthly take-home target
                if (story === 'target-mode') {
                  const monthlyTarget = parseFloat(formData.monthlyTarget) || 0;
                  const weeksOff = 52 - workWeeks;
                  const federalTaxRate = (formData.federalTaxRate || 0) / 100;
                  const totalTaxRate = SELF_EMPLOYMENT_TAX + federalTaxRate;
                  // monthlyTarget IS the net (take-home). Gross is derived by adding taxes on top.
                  const monthlyNet = monthlyTarget;
                  const monthlyGross = totalTaxRate < 1 ? monthlyTarget / (1 - totalTaxRate) : monthlyTarget;
                  const timeOffBufferPerWeek = getTimeOffBuffer();
                  const totalTimeOffFund = timeOffBufferPerWeek * workWeeks;
                  const annualGross = monthlyGross * 12;
                  const annualNet = monthlyNet * 12;
                  
                  return (
                    <div className="max-w-4xl mx-auto mt-16" data-testid="timeline-section">
                      {/* Dark background container for target-mode trajectory */}
                      <div className="p-8 sm:p-10 rounded-2xl" style={{ background: '#1E1640' }}>
                        <div className="text-center mb-8">
                          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full mb-4" style={{ background: 'rgba(245, 203, 92, 0.15)' }}>
                            <Target className="w-4 h-4" style={{ color: '#F5CB5C' }} />
                            <span className="font-body text-sm" style={{ color: '#F5CB5C' }}>Your Income Target</span>
                          </div>
                          <h3 className="font-heading text-2xl sm:text-3xl font-medium mb-2" style={{ color: '#FFFFFF' }}>
                            Your path to taking home {formatCurrency(monthlyTarget)}/month.
                          </h3>
                          <p className="font-body text-sm" style={{ color: 'rgba(255,255,255,0.6)' }}>
                            Whether it's for rent, residency, or a qualification, here's what it takes.
                          </p>
                        </div>
                        <div className="relative h-3 rounded-full overflow-hidden mb-8" style={{ background: 'rgba(255,255,255,0.1)' }}>
                          <div className="absolute h-full w-full" style={{ background: '#F5CB5C' }} />
                        </div>
                        <div className="p-8 rounded-xl text-center" style={{ background: 'rgba(255,255,255,0.05)', border: '2px solid #F5CB5C' }}>
                          <p className="font-body text-xs uppercase tracking-wider mb-4" style={{ color: '#F5CB5C' }}>Your Weekly Target</p>
                          <p className="font-heading text-5xl font-medium mb-2" style={{ color: '#FFFFFF' }}>{formatCurrency(weeklyTarget)}</p>
                          <p className="font-heading text-xl mb-4" style={{ color: '#F5CB5C' }}>{formatCurrency(dailyTarget)}/day × {daysPerWeek} days</p>
                          
                          {weeksOff > 0 && (
                            <p className="font-body text-sm mb-6" style={{ color: 'rgba(255,255,255,0.7)' }}>
                              Includes each week: <span style={{ color: '#FFFFFF' }}>{formatCurrency(timeOffBufferPerWeek)} towards time off</span>
                            </p>
                          )}
                          
                          {/* Monthly/Annual breakdown */}
                          <div className="mb-6 p-4 rounded-lg" style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)' }}>
                            <div className="grid grid-cols-2 gap-4 text-center">
                              <div>
                                <p className="font-body text-xs uppercase mb-1" style={{ color: 'rgba(255,255,255,0.5)' }}>Monthly Gross</p>
                                <p className="font-heading text-xl" style={{ color: '#FFFFFF' }}>{formatCurrency(monthlyGross)}</p>
                              </div>
                              <div>
                                <p className="font-body text-xs uppercase mb-1" style={{ color: 'rgba(255,255,255,0.5)' }}>Annual Gross</p>
                                <p className="font-heading text-xl" style={{ color: '#FFFFFF' }}>{formatCurrency(annualGross)}</p>
                              </div>
                            </div>
                            <div className="mt-4 pt-4 border-t" style={{ borderColor: 'rgba(255,255,255,0.1)' }}>
                              <p className="font-body text-xs mb-1" style={{ color: 'rgba(255,255,255,0.5)' }}>After taxes (~{Math.round(totalTaxRate * 100)}%), you'll take home:</p>
                              <p className="font-heading text-lg" style={{ color: '#4CAF50' }}>{formatCurrency(annualNet)}/year ({formatCurrency(monthlyNet)}/month)</p>
                            </div>
                          </div>
                          
                          {weeksOff > 0 && (
                            <div className="mb-6 p-4 rounded-lg" style={{ background: 'rgba(255,255,255,0.05)' }}>
                              <p className="font-body text-sm mb-3 font-medium" style={{ color: '#FFFFFF' }}>Your {weeksOff}-week time-off fund</p>
                              <div className="flex justify-center gap-8 text-center">
                                <div>
                                  <p className="font-body text-xs mb-1" style={{ color: 'rgba(255,255,255,0.5)' }}>Total saved</p>
                                  <p className="font-heading text-xl font-medium" style={{ color: '#FFFFFF' }}>{formatCurrency(totalTimeOffFund)}</p>
                                </div>
                                <div>
                                  <p className="font-body text-xs mb-1" style={{ color: 'rgba(255,255,255,0.5)' }}>For each week you take off</p>
                                  <p className="font-heading text-xl font-medium" style={{ color: '#FFFFFF' }}>{formatCurrency(totalTimeOffFund / weeksOff)}</p>
                                </div>
                              </div>
                            </div>
                          )}
                          
                          <div className="pt-6 border-t" style={{ borderColor: 'rgba(255,255,255,0.1)' }}>
                            <p className="font-body text-base" style={{ color: 'rgba(255,255,255,0.9)' }}>{workWeeks} weeks of hitting this target = <span style={{ color: '#F5CB5C' }}>your take-home goal of {formatCurrency(monthlyTarget)}/month achieved</span></p>
                          </div>
                        </div>
                        <p className="font-body text-sm text-center mt-4" style={{ color: 'rgba(255,255,255,0.5)' }}>This calculation accounts for self-employment tax and federal income tax so you know the real number to hit.</p>
                      </div>
                    </div>
                  );
                }

                // STORY 2: Debt Only - Two chapters
                if (story === 'debt-only') {
                  const debtMonths = parseInt(formData.debtMonths) || 6;
                  const debtAmount = parseFloat(formData.debtAmount) || 0;
                  const federalTaxDecimal = (formData.federalTaxRate || 10) / 100;
                  const totalTaxRate = 0.153 + federalTaxDecimal;
                  const baseWeekly = (parseFloat(formData.monthlyExpenses) * 12 / workWeeks) / (1 - totalTaxRate);
                  const baseDaily = baseWeekly / daysPerWeek;
                  const remainingMonths = 12 - debtMonths;
                  const remainingWeeks = Math.round((remainingMonths / 12) * workWeeks);
                  const extraPerWeekIfKeep = weeklyTarget - baseWeekly;
                  const potentialSavingsMax = extraPerWeekIfKeep * remainingWeeks;
                  const potentialSavingsMin = potentialSavingsMax * 0.5;
                  const weeksOff = 52 - workWeeks;
                  const timeOffBufferPerWeek = getTimeOffBuffer();
                  const totalTimeOffFund = timeOffBufferPerWeek * workWeeks;
                  const netWeeklyNeed = calculateNetWeeklyTarget();
                  const ptoFundIfKeep = extraPerWeekIfKeep * (1 - totalTaxRate) * remainingWeeks;
                  const extraWeeksOffFunded = netWeeklyNeed > 0 ? ptoFundIfKeep / netWeeklyNeed : 0;
                  return (
                    <div className="max-w-5xl mx-auto mt-16" data-testid="timeline-section">
                      <div className="text-center mb-8">
                        <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full mb-4" style={{ background: 'rgba(222, 60, 75, 0.15)' }}>
                          <Calendar className="w-4 h-4" style={{ color: '#DE3C4B' }} />
                          <span className="font-body text-sm" style={{ color: '#DE3C4B' }}>Your 12-Month Trajectory</span>
                        </div>
                        <h3 className="font-heading text-2xl sm:text-3xl font-medium mb-2" style={{ color: '#2A1E5C' }}>Two chapters to your year.</h3>
                        <p className="font-body text-sm" style={{ color: '#5D5A6B' }}>Pay off the debt, then unlock new possibilities.</p>
                      </div>
                      
                      {/* Time-off fund summary */}
                      {weeksOff > 0 && (
                        <div className="mb-6 p-4 rounded-lg max-w-3xl mx-auto" style={{ background: 'rgba(42, 30, 92, 0.05)' }}>
                          <p className="font-body text-sm text-center mb-3 font-medium" style={{ color: '#2A1E5C' }}>Your {weeksOff}-week time-off fund</p>
                          <div className="flex justify-center gap-8 text-center">
                            <div>
                              <p className="font-heading text-lg font-medium" style={{ color: '#2A1E5C' }}>{formatCurrency(timeOffBufferPerWeek)}/week</p>
                              <p className="font-body text-xs" style={{ color: '#8B7E6A' }}>Towards time off</p>
                            </div>
                            <div>
                              <p className="font-heading text-lg font-medium" style={{ color: '#2A1E5C' }}>{formatCurrency(totalTimeOffFund)}</p>
                              <p className="font-body text-xs" style={{ color: '#8B7E6A' }}>Total saved</p>
                            </div>
                            <div>
                              <p className="font-heading text-lg font-medium" style={{ color: '#2A1E5C' }}>{formatCurrency(totalTimeOffFund / weeksOff)}</p>
                              <p className="font-body text-xs" style={{ color: '#8B7E6A' }}>For each week you take off</p>
                            </div>
                          </div>
                        </div>
                      )}
                      
                      <div className="relative h-3 rounded-full overflow-hidden mb-8" style={{ background: 'rgba(42, 30, 92, 0.1)' }}>
                        <div className="absolute h-full" style={{ left: '0%', width: `${(debtMonths / 12) * 100}%`, background: '#DE3C4B', borderRight: '2px solid rgba(255,255,255,0.3)' }} />
                        <div className="absolute h-full" style={{ left: `${(debtMonths / 12) * 100}%`, width: `${100 - (debtMonths / 12) * 100}%`, background: '#4CAF50' }} />
                      </div>
                      <div className="grid md:grid-cols-2 gap-6">
                        {/* Chapter 1: Payoff */}
                        <div className="p-6 rounded-xl flex flex-col" style={{ background: 'rgba(42, 30, 92, 0.05)', border: '2px solid #DE3C4B' }}>
                          <div className="flex items-center justify-between mb-6">
                            <span className="font-heading text-sm uppercase tracking-wider font-medium" style={{ color: '#DE3C4B' }}>Chapter 1: Payoff</span>
                            <span className="font-body text-xs" style={{ color: '#8B7E6A' }}>Months 1-{debtMonths}</span>
                          </div>
                          <div className="text-center flex-1 flex flex-col justify-center mb-6">
                            <p className="font-heading text-4xl lg:text-5xl font-medium mb-2" style={{ color: '#2A1E5C' }}>{formatCurrency(weeklyTarget)}</p>
                            <p className="font-heading text-lg" style={{ color: '#5D5A6B' }}>per week</p>
                            <div className="mt-4 pt-4 border-t" style={{ borderColor: 'rgba(42, 30, 92, 0.1)' }}>
                              <p className="font-heading text-2xl font-medium" style={{ color: '#DE3C4B' }}>{formatCurrency(dailyTarget)}/day</p>
                              <p className="font-body text-sm mt-1" style={{ color: '#5D5A6B' }}>× {daysPerWeek} days a week</p>
                            </div>
                          </div>
                          <div className="pt-4 border-t mt-auto text-center" style={{ borderColor: 'rgba(42, 30, 92, 0.1)' }}>
                            <CheckCircle2 className="w-6 h-6 mx-auto mb-2" style={{ color: '#DE3C4B' }} />
                            <span className="font-body text-sm" style={{ color: '#5D5A6B' }}>{formatCurrency(debtAmount)} debt cleared by</span>
                            <p className="font-heading text-2xl font-medium" style={{ color: '#DE3C4B' }}>Month {debtMonths}</p>
                          </div>
                        </div>
                        
                        {/* Chapter 2: Freedom */}
                        <div className="p-6 rounded-xl flex flex-col" style={{ background: 'rgba(76, 175, 80, 0.08)', border: '2px solid #4CAF50' }}>
                          <div className="flex items-center justify-between mb-4">
                            <span className="font-heading text-sm uppercase tracking-wider font-medium" style={{ color: '#4CAF50' }}>Chapter 2: Freedom</span>
                            <span className="font-body text-xs" style={{ color: '#8B7E6A' }}>Months {debtMonths + 1}-12</span>
                          </div>
                          
                          <div className="text-center mb-4">
                            <p className="font-body text-xs uppercase tracking-wider mb-2" style={{ color: '#8B7E6A' }}>Now all you need to cover expenses</p>
                            <p className="font-heading text-3xl font-medium" style={{ color: '#2A1E5C' }}>{formatCurrency(baseWeekly)}</p>
                            <p className="font-heading text-base" style={{ color: '#4CAF50' }}>{formatCurrency(baseDaily)}/day × {daysPerWeek} days</p>
                          </div>
                          
                          <div className="pt-4 border-t" style={{ borderColor: 'rgba(42, 30, 92, 0.1)' }}>
                            <p className="font-body text-xs text-center mb-3" style={{ color: '#5D5A6B' }}>
                              You said you weren't focusing on savings this year, but after your debt's paid off, here's what's possible:
                            </p>
                            
                            {/* Keep Momentum Option */}
                            <div className="p-4 rounded-lg mb-3" style={{ background: 'rgba(245, 203, 92, 0.15)' }}>
                              <p className="font-heading text-sm font-medium mb-2" style={{ color: '#F5CB5C' }}>Keep Your Momentum</p>
                              <p className="font-body text-xs mb-3" style={{ color: '#5D5A6B' }}>
                                Keep your original target of {formatCurrency(weeklyTarget)}/wk and put the extra towards savings.
                              </p>
                              <div className="text-center p-3 rounded-lg" style={{ background: 'rgba(42, 30, 92, 0.05)' }}>
                                <p className="font-body text-xs mb-1" style={{ color: '#8B7E6A' }}>Potential savings by year-end</p>
                                <p className="font-heading text-2xl font-medium" style={{ color: '#F5CB5C' }}>{formatCurrency(potentialSavingsMax)}</p>
                                <p className="font-body text-xs" style={{ color: '#8B7E6A' }}>in {remainingMonths} months</p>
                              </div>
                            </div>
                            
                            {/* Flexibility Option */}
                            <div className="p-4 rounded-lg mb-3" style={{ background: 'rgba(255, 255, 255, 0.05)' }}>
                              <p className="font-heading text-sm font-medium mb-2" style={{ color: '#2A1E5C' }}>More Breathing Room</p>
                              <p className="font-body text-xs mb-2" style={{ color: '#5D5A6B' }}>
                                Lower your target to between {formatCurrency(baseWeekly)}/wk and {formatCurrency(weeklyTarget)}/wk.
                              </p>
                              <p className="font-body text-xs" style={{ color: '#5D5A6B' }}>
                                Potential savings: {formatCurrency(0)} – {formatCurrency(potentialSavingsMax)}
                              </p>
                            </div>
                            
                            {/* Buy Back Time Option */}
                            <div className="p-4 rounded-lg" style={{ background: 'rgba(76, 175, 80, 0.12)' }} data-testid="buy-back-time-option">
                              <p className="font-heading text-sm font-medium mb-2" style={{ color: '#4CAF50' }}>Buy Back Time</p>
                              <p className="font-body text-xs mb-2" style={{ color: '#5D5A6B' }}>
                                Keep your original target and put the extra toward time off instead.
                              </p>
                              <p className="font-body text-xs" style={{ color: '#5D5A6B' }}>
                                That's about <strong style={{ color: '#2A1E5C' }}>{extraWeeksOffFunded.toFixed(1)} extra weeks off</strong>, fully funded, {formatCurrency(ptoFundIfKeep)} in your time-off fund by year-end.
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                }

                // STORY 3: Savings Only - Accumulation over year
                if (story === 'savings-only') {
                  const savingsGoal = parseFloat(formData.savingsGoal) || 0;
                  const weeksOff = 52 - workWeeks;
                  const timeOffBufferPerWeek = getTimeOffBuffer();
                  const totalTimeOffFund = timeOffBufferPerWeek * workWeeks;
                  const weeklySavingsAmount = savingsGoal / workWeeks;
                  const milestoneLabels = ['Q1 checkpoint', 'Halfway', 'Momentum', 'Goal reached'];
                  return (
                    <div className="max-w-4xl mx-auto mt-16" data-testid="timeline-section">
                      <div className="text-center mb-8">
                        <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full mb-4" style={{ background: 'rgba(46, 134, 171, 0.15)' }}>
                          <Calendar className="w-4 h-4" style={{ color: '#2E86AB' }} />
                          <span className="font-body text-sm" style={{ color: '#2E86AB' }}>Your 12-Month Trajectory</span>
                        </div>
                        <h3 className="font-heading text-2xl sm:text-3xl font-medium mb-2" style={{ color: '#2A1E5C' }}>Building your safety net, week by week.</h3>
                        <p className="font-body text-sm" style={{ color: '#5D5A6B' }}>Small consistent deposits add up to big security.</p>
                      </div>
                      
                      <div className="relative h-3 rounded-full overflow-hidden mb-8" style={{ background: 'rgba(42, 30, 92, 0.1)' }}>
                        <div className="absolute h-full w-full" style={{ background: 'linear-gradient(to right, #2E86AB 0%, #4CAF50 100%)' }} />
                      </div>
                      
                      <div className="p-8 rounded-xl" style={{ background: 'rgba(46, 134, 171, 0.08)', border: '2px solid #2E86AB' }}>
                        <div className="text-center mb-6">
                          <p className="font-body text-xs uppercase tracking-wider mb-4" style={{ color: '#2E86AB' }}>Your Weekly Target</p>
                          <p className="font-heading text-5xl font-medium mb-2" style={{ color: '#2A1E5C' }}>{formatCurrency(weeklyTarget)}</p>
                          <p className="font-heading text-xl mb-4" style={{ color: '#2E86AB' }}>{formatCurrency(dailyTarget)}/day × {daysPerWeek} days</p>
                          
                          {/* Included each week - clarifies relationship */}
                          <p className="font-body text-sm" style={{ color: '#5D5A6B' }}>
                            Includes each week: <span style={{ color: '#4CAF50' }}>{formatCurrency(weeklySavingsAmount)} towards savings</span>
                            {weeksOff > 0 && <span> + <span style={{ color: '#2A1E5C' }}>{formatCurrency(timeOffBufferPerWeek)} towards time off</span></span>}
                          </p>
                        </div>
                        
                        {/* Quarterly Progress with milestone labels */}
                        <div className="grid grid-cols-4 gap-4 pt-6 border-t" style={{ borderColor: 'rgba(42, 30, 92, 0.1)' }}>
                          {[3, 6, 9, 12].map((month, idx) => (
                            <div key={month} className="text-center">
                              <p className="font-body text-xs mb-1" style={{ color: month === 12 ? '#4CAF50' : 'rgba(255,255,255,0.5)' }}>{milestoneLabels[idx]}</p>
                              <p className="font-heading text-lg font-medium" style={{ color: month === 12 ? '#4CAF50' : '#FFFFFF' }}>{formatCurrency((savingsGoal / 12) * month)}</p>
                              <p className="font-body text-xs" style={{ color: '#8B7E6A' }}>Month {month}</p>
                            </div>
                          ))}
                        </div>
                        
                        {/* Final Results */}
                        <div className="mt-6 pt-4 border-t" style={{ borderColor: 'rgba(42, 30, 92, 0.1)' }}>
                          <div className="flex items-center justify-center gap-2 mb-2">
                            <CheckCircle2 className="w-5 h-5" style={{ color: '#4CAF50' }} />
                            <p className="font-body text-base" style={{ color: '#2A1E5C' }}>By month 12, you'll have <span className="font-medium" style={{ color: '#4CAF50' }}>{formatCurrency(savingsGoal)} in your safety net</span></p>
                          </div>
                          {weeksOff > 0 && (
                            <div className="mt-4 p-4 rounded-lg" style={{ background: 'rgba(42, 30, 92, 0.05)' }}>
                              <p className="font-body text-sm text-center mb-3 font-medium" style={{ color: '#2A1E5C' }}>Your {weeksOff}-week time-off fund</p>
                              <div className="flex justify-center gap-8 text-center">
                                <div>
                                  <p className="font-body text-xs mb-1" style={{ color: '#8B7E6A' }}>Total saved</p>
                                  <p className="font-heading text-xl font-medium" style={{ color: '#2A1E5C' }}>{formatCurrency(totalTimeOffFund)}</p>
                                </div>
                                <div>
                                  <p className="font-body text-xs mb-1" style={{ color: '#8B7E6A' }}>For each week you take off</p>
                                  <p className="font-heading text-xl font-medium" style={{ color: '#2A1E5C' }}>{formatCurrency(totalTimeOffFund / weeksOff)}</p>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                }

                // STORY 4: Debt + Savings - Full three-phase story
                if (story === 'debt-and-savings' && calculateMomentumOptions()) {
                  const options = calculateMomentumOptions();
                  const weeksOff = 52 - workWeeks;
                  const timeOffBufferPerWeek = getTimeOffBuffer();
                  const totalTimeOffFund = timeOffBufferPerWeek * workWeeks;
                  return (
                    <div className="max-w-6xl mx-auto mt-16" data-testid="timeline-section">
                      <div className="text-center mb-8">
                        <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full mb-4" style={{ background: 'rgba(46, 134, 171, 0.15)' }}>
                          <Calendar className="w-4 h-4" style={{ color: '#2E86AB' }} />
                          <span className="font-body text-sm" style={{ color: '#2E86AB' }}>Your 12-Month Trajectory</span>
                        </div>
                        <h3 className="font-heading text-2xl sm:text-3xl font-medium mb-2" style={{ color: '#2A1E5C' }}>Here's how your year unfolds.</h3>
                        <p className="font-body text-sm" style={{ color: '#5D5A6B' }}>After you clear your debt, you unlock new choices.</p>
                      </div>
                      
                      {/* Time-off fund summary */}
                      {weeksOff > 0 && (
                        <div className="mb-6 p-4 rounded-lg max-w-3xl mx-auto" style={{ background: 'rgba(42, 30, 92, 0.05)' }}>
                          <p className="font-body text-sm text-center mb-3 font-medium" style={{ color: '#2A1E5C' }}>Your {weeksOff}-week time-off fund</p>
                          <div className="flex justify-center gap-8 text-center">
                            <div>
                              <p className="font-heading text-lg font-medium" style={{ color: '#2A1E5C' }}>{formatCurrency(timeOffBufferPerWeek)}/week</p>
                              <p className="font-body text-xs" style={{ color: '#8B7E6A' }}>Towards time off</p>
                            </div>
                            <div>
                              <p className="font-heading text-lg font-medium" style={{ color: '#2A1E5C' }}>{formatCurrency(totalTimeOffFund)}</p>
                              <p className="font-body text-xs" style={{ color: '#8B7E6A' }}>Total saved</p>
                            </div>
                            <div>
                              <p className="font-heading text-lg font-medium" style={{ color: '#2A1E5C' }}>{formatCurrency(totalTimeOffFund / weeksOff)}</p>
                              <p className="font-body text-xs" style={{ color: '#8B7E6A' }}>For each week you take off</p>
                            </div>
                          </div>
                        </div>
                      )}
                      
                      <div className="relative h-3 rounded-full overflow-hidden mb-8" style={{ background: 'rgba(42, 30, 92, 0.1)' }}>
                        <div className="absolute h-full" style={{ left: '0%', width: '33.33%', background: '#2A1E5C', borderRight: '2px solid rgba(255,255,255,0.3)' }} />
                        <div className="absolute h-full" style={{ left: '33.33%', width: '33.33%', background: '#2E86AB', borderRight: '2px solid rgba(255,255,255,0.3)' }} />
                        <div className="absolute h-full" style={{ left: '66.66%', width: '33.34%', background: '#4CAF50' }} />
                      </div>
                      <div className="grid md:grid-cols-3 gap-6">
                        <div className="p-6 rounded-xl flex flex-col" style={{ background: 'rgba(42, 30, 92, 0.08)', border: '2px solid #2A1E5C' }}>
                          <div className="flex items-center justify-between mb-6">
                            <span className="font-heading text-sm uppercase tracking-wider font-medium" style={{ color: '#2A1E5C' }}>Foundation</span>
                            <span className="font-body text-xs" style={{ color: '#8B7E6A' }}>Months 1-{options.phase1.endMonth}</span>
                          </div>
                          <div className="text-center flex-1 flex flex-col justify-center mb-6">
                            <p className="font-heading text-4xl lg:text-5xl font-medium mb-2" style={{ color: '#2A1E5C' }}>{formatCurrency(options.phase1.grossWeekly)}</p>
                            <p className="font-heading text-lg" style={{ color: '#5D5A6B' }}>per week</p>
                            <div className="mt-4 pt-4 border-t" style={{ borderColor: 'rgba(42, 30, 92, 0.1)' }}>
                              <p className="font-heading text-2xl font-medium" style={{ color: '#2A1E5C' }}>{formatCurrency(options.phase1.grossDaily)}/day</p>
                              <p className="font-body text-sm mt-1" style={{ color: '#5D5A6B' }}>× {daysPerWeek} days a week</p>
                            </div>
                          </div>
                          <div className="space-y-2 mb-4 pt-4 border-t" style={{ borderColor: 'rgba(42, 30, 92, 0.1)' }}>
                            <p className="font-body text-xs" style={{ color: '#8B7E6A' }}>Each week you're setting aside:</p>
                            {options.phase1.activeGoals.map((goal) => (
                              <div key={`phase1-${goal.name}`} className="flex justify-between items-center">
                                <span className="font-body text-sm" style={{ color: '#5D5A6B' }}>{goal.name}</span>
                                <span className="font-body text-sm font-medium" style={{ color: '#2A1E5C' }}>{formatCurrency(goal.weeklyAmount)}/wk</span>
                              </div>
                            ))}
                          </div>
                          <div className="pt-4 border-t mt-auto text-center" style={{ borderColor: 'rgba(42, 30, 92, 0.1)' }}>
                            <CheckCircle2 className="w-6 h-6 mx-auto mb-2" style={{ color: '#2A1E5C' }} />
                            <span className="font-body text-sm" style={{ color: '#5D5A6B' }}>Debt cleared by</span>
                            <p className="font-heading text-2xl font-medium" style={{ color: '#2A1E5C' }}>Month {options.phase1.endMonth}</p>
                          </div>
                        </div>
                        <div className="p-6 rounded-xl flex flex-col" style={{ background: 'rgba(46, 134, 171, 0.08)', border: '2px solid #2E86AB' }}>
                          <div className="flex items-center justify-between mb-4">
                            <span className="font-heading text-sm uppercase tracking-wider font-medium" style={{ color: '#2E86AB' }}>Momentum</span>
                            <span className="font-body text-xs" style={{ color: '#8B7E6A' }}>Months {options.phase1.endMonth + 1}-12</span>
                          </div>
                          <p className="font-heading text-lg mb-4" style={{ color: '#2A1E5C' }}>Debt's done. Now you have options:</p>
                          <div className="p-4 rounded-lg mb-3 text-center" style={{ background: 'rgba(76, 175, 80, 0.15)' }}>
                            <p className="font-body text-xs font-medium mb-1" style={{ color: '#4CAF50' }}>Path A</p>
                            <p className="font-heading text-sm font-medium mb-2" style={{ color: '#2A1E5C' }}>Keep Your Momentum</p>
                            <p className="font-body text-sm" style={{ color: '#5D5A6B' }}>{formatCurrency(options.optionA.dailyTarget)}/day × {options.daysPerWeek} days = {formatCurrency(options.optionA.weeklyTarget)}/wk</p>
                            <p className="font-body text-xs mt-1" style={{ color: '#5D5A6B' }}>Extra {formatCurrency(options.optionA.extraPerWeek)}/wk goes to savings</p>
                          </div>
                          <div className="p-4 rounded-lg mb-3 text-center" style={{ background: 'rgba(46, 134, 171, 0.15)' }}>
                            <p className="font-body text-xs font-medium mb-1" style={{ color: '#2E86AB' }}>Path B</p>
                            <p className="font-heading text-sm font-medium mb-2" style={{ color: '#2A1E5C' }}>Give Yourself Flexibility</p>
                            <p className="font-body text-sm" style={{ color: '#5D5A6B' }}>Lower your target to {formatCurrency(options.optionB.weeklyTargetLow)}/wk – {formatCurrency(options.optionB.weeklyTargetHigh)}/wk</p>
                            <p className="font-body text-xs mt-1" style={{ color: '#5D5A6B' }}>You choose your pace</p>
                          </div>
                          <div className="p-4 rounded-lg mb-3 text-center" style={{ background: 'rgba(20, 184, 166, 0.15)' }}>
                            <p className="font-body text-xs font-medium mb-1" style={{ color: '#14B8A6' }}>Path C</p>
                            <p className="font-heading text-sm font-medium mb-2" style={{ color: '#2A1E5C' }}>Cover Your Expenses</p>
                            <p className="font-body text-sm" style={{ color: '#5D5A6B' }}>{formatCurrency(options.optionC.dailyTarget)}/day × {options.optionC.daysPerWeek} days = {formatCurrency(options.optionC.weeklyTarget)}/wk</p>
                            <p className="font-body text-xs mt-1" style={{ color: '#5D5A6B' }}>Meet your savings goal exactly</p>
                          </div>
                          <div className="p-4 rounded-lg text-center" style={{ background: 'rgba(245, 203, 92, 0.18)' }} data-testid="buy-back-time-option">
                            <p className="font-body text-xs font-medium mb-1" style={{ color: '#B8952E' }}>Path D</p>
                            <p className="font-heading text-sm font-medium mb-2" style={{ color: '#2A1E5C' }}>Buy Back Time</p>
                            <p className="font-body text-sm" style={{ color: '#5D5A6B' }}>{formatCurrency(options.optionD.dailyTarget)}/day × {options.optionD.daysPerWeek} days = {formatCurrency(options.optionD.weeklyTarget)}/wk</p>
                            <p className="font-body text-xs mt-1" style={{ color: '#5D5A6B' }}>Extra {formatCurrency(options.optionD.extraPerWeek)}/wk goes to your time-off fund</p>
                          </div>
                        </div>
                        <div className="p-6 rounded-xl flex flex-col" style={{ background: 'rgba(76, 175, 80, 0.08)', border: '2px solid #4CAF50' }}>
                          <div className="flex items-center justify-between mb-4">
                            <span className="font-heading text-sm uppercase tracking-wider font-medium" style={{ color: '#4CAF50' }}>Year-End Results</span>
                            <span className="font-body text-xs" style={{ color: '#8B7E6A' }}>At Month 12</span>
                          </div>
                          <p className="font-heading text-lg mb-4" style={{ color: '#2A1E5C' }}>Where each path lands you:</p>
                          <div className="p-4 rounded-lg mb-3 text-center" style={{ background: 'rgba(76, 175, 80, 0.15)' }}>
                            <p className="font-body text-xs font-medium mb-1" style={{ color: '#4CAF50' }}>Path A</p>
                            <p className="font-heading text-2xl font-medium" style={{ color: '#2A1E5C' }}>{formatCurrency(options.optionA.totalSavedByYearEnd)}</p>
                            <p className="font-body text-xs" style={{ color: '#5D5A6B' }}>in savings</p>
                            <p className="font-body text-xs mt-1" style={{ color: '#4CAF50' }}>(+{formatCurrency(options.optionA.extraSavings)} beyond goal)</p>
                          </div>
                          <div className="p-4 rounded-lg mb-3 text-center" style={{ background: 'rgba(46, 134, 171, 0.15)' }}>
                            <p className="font-body text-xs font-medium mb-1" style={{ color: '#2E86AB' }}>Path B</p>
                            <p className="font-heading text-lg font-medium" style={{ color: '#2A1E5C' }}>{formatCurrency(options.optionB.totalSavedLow)} – {formatCurrency(options.optionB.totalSavedHigh)}</p>
                            <p className="font-body text-xs" style={{ color: '#5D5A6B' }}>in savings</p>
                            <p className="font-body text-xs mt-1" style={{ color: '#8B7E6A' }}>(you choose your pace)</p>
                          </div>
                          <div className="p-4 rounded-lg mb-3 text-center" style={{ background: 'rgba(20, 184, 166, 0.15)' }}>
                            <p className="font-body text-xs font-medium mb-1" style={{ color: '#14B8A6' }}>Path C</p>
                            <p className="font-heading text-2xl font-medium" style={{ color: '#2A1E5C' }}>{formatCurrency(options.optionC.totalSavedByYearEnd)}</p>
                            <p className="font-body text-xs" style={{ color: '#5D5A6B' }}>in savings</p>
                            <p className="font-body text-xs mt-1" style={{ color: '#8B7E6A' }}>(goal met exactly)</p>
                          </div>
                          <div className="p-4 rounded-lg text-center" style={{ background: 'rgba(245, 203, 92, 0.18)' }}>
                            <p className="font-body text-xs font-medium mb-1" style={{ color: '#B8952E' }}>Path D</p>
                            <p className="font-heading text-2xl font-medium" style={{ color: '#2A1E5C' }}>+{options.optionD.extraWeeksOff.toFixed(1)} weeks off</p>
                            <p className="font-body text-xs" style={{ color: '#5D5A6B' }}>fully funded time off</p>
                            <p className="font-body text-xs mt-1" style={{ color: '#8B7E6A' }}>({formatCurrency(options.optionD.ptoFundByYearEnd)} to your time-off fund, goal still met)</p>
                          </div>
                          <p className="font-body text-xs text-center mt-auto pt-4" style={{ color: '#8B7E6A' }}>All paths clear your debt and meet your savings goal.</p>
                        </div>
                      </div>
                    </div>
                  );
                }
                return null;
              })()}
            </div>
          )}
        </div>
      </section>

      {/* PDF Download & Next Steps Section */}
      {showResults && (
        <section id="download-section" className="pt-8 pb-12 relative z-10">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            
            {/* Try Again or Download Choice - Hidden after email sent */}
            {!emailSent && (
              <div className="text-center mb-8 p-6 rounded-2xl" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.1)' }}>
                <p className="font-heading text-xl mb-4" style={{ color: '#2A1E5C' }}>
                  Want to try different numbers before you download?
                </p>
                <button
                  onClick={() => {
                    setShowResults(false);
                    setCurrentStep(0);
                    window.scrollTo({ top: document.getElementById('calculator')?.offsetTop - 100, behavior: 'smooth' });
                  }}
                  className="px-6 py-3 rounded-xl font-heading font-medium inline-flex items-center gap-2 transition-all hover:scale-105 mb-4"
                  style={{ 
                    background: 'rgba(42, 30, 92, 0.1)', 
                    color: '#2A1E5C',
                    border: '1px solid rgba(255,255,255,0.2)'
                  }}
                >
                  <ArrowLeft className="w-4 h-4" />
                  Run Another Scenario
                </button>
                <p className="font-body text-sm" style={{ color: '#8B7E6A' }}>
                  Or scroll down to download these results.
                </p>
              </div>
            )}

            {!hasDownloadedPDF ? (
              /* Download Section - One Time Only */
              <div className="p-8 rounded-3xl" style={{ 
                background: 'linear-gradient(135deg, rgba(245, 203, 92, 0.15) 0%, rgba(46, 134, 171, 0.1) 100%)', 
                border: '2px solid rgba(245, 203, 92, 0.3)',
                boxShadow: '0 8px 32px rgba(245, 203, 92, 0.15)'
              }}>
                {/* Header - centered */}
                <div className="text-center mb-6">
                  <div className="inline-flex items-center justify-center w-14 h-14 rounded-full mb-3" style={{ background: 'rgba(245, 203, 92, 0.2)', border: '2px solid rgba(245, 203, 92, 0.4)' }}>
                    <Download className="w-7 h-7" style={{ color: '#F5CB5C' }} />
                  </div>
                  <h3 className="font-heading text-2xl font-medium mb-2" style={{ color: '#2A1E5C' }}>
                    Ready to download your results?
                  </h3>
                  <p className="font-body text-base" style={{ color: '#5D5A6B' }}>
                    Your weekly target, tax breakdown, and 12-month trajectory delivered as a PDF you can keep.
                  </p>
                </div>
                
                {/* Tweak note */}
                <p className="font-body text-sm text-center mb-4" style={{ color: '#8B7E6A' }}>
                  Happy with your numbers? Enter your email below to receive your personalized PDF. <strong>You can only download once.</strong>
                </p>
                
                {/* Form row */}
                <div className="flex flex-col sm:flex-row gap-4 max-w-lg mx-auto">
                  <input
                    type="email"
                    value={emailInput}
                    onChange={(e) => setEmailInput(e.target.value)}
                    placeholder="Enter your email"
                    className="flex-1 px-5 py-4 rounded-xl font-body text-lg transition-all focus:ring-2 focus:ring-yellow-400"
                    style={{ 
                      background: '#FFFFFF', 
                      border: '2px solid #2A1E5C',
                      color: '#2A1E5C',
                      outline: 'none'
                    }}
                    data-testid="email-input"
                  />
                  
                  <button
                    onClick={handleDownloadPDF}
                    disabled={isDownloadingPDF || !emailInput}
                    className="px-8 py-4 rounded-xl font-heading font-medium text-lg flex items-center justify-center gap-2 transition-all hover:scale-105"
                    style={{ 
                      backgroundColor: isDownloadingPDF ? '#D4B84A' : '#F5CB5C', 
                      color: '#1a1a2e',
                      opacity: !emailInput ? 0.5 : 1,
                      cursor: isDownloadingPDF || !emailInput ? 'not-allowed' : 'pointer',
                      whiteSpace: 'nowrap',
                      boxShadow: emailInput && !isDownloadingPDF ? '0 6px 24px rgba(245, 203, 92, 0.5)' : 'none'
                    }}
                    data-testid="download-pdf-btn"
                  >
                    {isDownloadingPDF ? (
                      <>
                        <Loader2 className="w-5 h-5 animate-spin" />
                        Generating...
                      </>
                    ) : (
                      <>
                        <Download className="w-5 h-5" />
                        Get My PDF
                      </>
                    )}
                  </button>
                </div>
                
                {/* Privacy text */}
                <p className="font-body text-xs text-center mt-4" style={{ color: '#8B7E6A' }}>
                  We never share your email with another party.
                </p>
              </div>
            ) : (
              /* Thank You + Next Steps Section */
              <div className="space-y-8">
                {/* Thank You Card */}
                <div className="p-8 rounded-3xl text-center" style={{ 
                  background: 'linear-gradient(135deg, rgba(76, 175, 80, 0.15) 0%, rgba(46, 134, 171, 0.1) 100%)', 
                  border: '2px solid rgba(76, 175, 80, 0.3)',
                  boxShadow: '0 8px 32px rgba(76, 175, 80, 0.15)'
                }}>
                  <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4" style={{ background: 'rgba(76, 175, 80, 0.2)' }}>
                    <CheckCircle2 className="w-8 h-8" style={{ color: '#4CAF50' }} />
                  </div>
                  <h3 className="font-heading text-3xl font-bold mb-2" style={{ color: '#4CAF50' }}>
                    Session complete!
                  </h3>
                  <p className="font-heading text-xl font-medium mb-4" style={{ color: '#2A1E5C' }}>
                    Your income mapping results are on the way.
                  </p>
                  <p className="font-body text-base mb-2" style={{ color: '#5D5A6B' }}>
                    Check your inbox at <span style={{ color: '#4CAF50' }}>{emailInput}</span>
                  </p>
                  <p className="font-body text-sm" style={{ color: '#8B7E6A' }}>
                    Your personalized PDF is attached. Save it somewhere you'll see it every week.
                  </p>
                </div>

                {/* 
                  ============================================
                  UPSELL FUNNEL TEMPORARILY DISABLED FOR TESTING
                  The sections below (Payday Relay $54, 4-Relay Challenge $9, 
                  Founder section, FAQs) have been commented out.
                  Re-enable by uncommenting when ready to test full funnel.
                  ============================================
                */}
              </div>
            )}
          </div>
        </section>
      )}

      {/* 
        ============================================
        FOUR RELAY CHALLENGE SECTION TEMPORARILY DISABLED
        Re-enable by uncommenting when ready
        ============================================
      */}

      {/* Creator + Footer */}
      <CreatorFooter />
      
      {/* Sticky Mobile CTA - positioned higher to avoid Emergent watermark */}
      {!hasPaidAccess && (
        <div className="fixed bottom-16 left-0 right-0 px-4 lg:hidden z-50">
          <button
            onClick={() => handleStartPayment('momentumMap')}
            disabled={isProcessingPayment}
            className="w-full py-4 rounded-xl font-heading font-bold text-lg flex items-center justify-center gap-2 transition-all"
            style={{ 
              background: isProcessingPayment ? '#D4B84A' : '#F5CB5C', 
              color: '#2A1E5C',
              boxShadow: '0 4px 20px rgba(245, 203, 92, 0.4)'
            }}
            data-testid="sticky-cta"
          >
            {isProcessingPayment ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                Processing...
              </>
            ) : (
              <>
                Get My Income Map, $4.95
              </>
            )}
          </button>
        </div>
      )}
      </>
      )}

      {/* Victory Lap Screen, post-Income-Map takeover */}
      {showVictoryLap && (
        <VictoryLapScreen
          weeklyTarget={calculateRelayTarget()}
          goalStatement={getGoalStatement()}
          copy={{ ctaPrimary: "Start my Free Sprint →", ctaSecondary: "See my full breakdown first" }}
          onCTAClick={() => {
            setShowVictoryLap(false);
            window.location.href = "/start/free-sprint";
          }}
          onDismiss={() => setShowVictoryLap(false)}
        />
      )}

      {/* Beta gate, direct checkout paused while in beta */}
      {gateModal}
    </div>
  );
};

export default LandingPage;
